import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Tabs,
    Tab,
    Drawer,
    Divider,
    Switch,
    FormControlLabel,
    Chip
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ReactSelect from 'react-select';
import AsyncSelect from 'react-select/async';
import '../../../node_modules/tabulator-tables/dist/css/tabulator_bootstrap4.css';
import { TabulatorFull as Tabulator, ColumnDefinition } from 'tabulator-tables';
import { useHttpClient, useAlert, getDataSetNamePBI } from '../modules/services/Bundle';
import { RequestMethod } from '../modules/services/core/_enums';
import { useFiltrosAtivos, FiltroGlobal } from '../../components/FiltrosGlobais';
import { useAuth } from '../modules/auth';
import { ICampoAssociacaoAPI, FieldType } from './administracao/organizações/types';
import HistoricTab, { HistoricoAtivo } from './HistoricTab';
import GerenciadorArquivosAssociacoes from '../../components/GerenciadorArquivosAssociacoes';

interface OrganizationOption {
    Id: number;
    OrganizationName: string;
    CorporationName: string;
}

interface AssociacaoAtivo {
    id: number;
    ativo: string;
    veiculo: string;
    associadoEm: string;
    desassociadoEm: string | null;
    duracaoMinutos: number;
    tempo: string; // Formato "dd.HH:mm:ss"
}

// FormData agora é dinâmico: mapeia LabelKey -> valor digitado pelo usuário
type FormDataDetalhes = Record<string, string>;

interface NovaAssociacaoPayload {
    Corporacao: string;
    Organizacao: string;
    Ativo: string;
    ItemAssociado: string;
    CampoChave: string;
}

// Interface para movimentações do TrackingAdjusted
interface IMovimentacaoHistorico {
    Corporacao: string;
    IdPrism: number;
    Organizacao: string;
    Ativo: string;
    PreviousLastSeen: string | null;
    CurrentLastSeen: string | null;
    StayMinutes: number;
    LastLatitude: number | null;
    LastLongitude: number | null;
    Status: string;
    Local: string | null;
    ItemAssociado: string | null;
    PosicaoAtual: number;
}

// Função para aplicar máscara manualmente
const applyMask = (value: string, mask: string): string => {
    if (!mask || !value) return value;

    let maskedValue = '';
    let valueIndex = 0;
    const cleanValue = value.replace(/[^0-9A-Za-z]/g, '');

    for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
        const maskChar = mask[i];
        if (maskChar === '9') {
            // Apenas números
            if (/[0-9]/.test(cleanValue[valueIndex])) {
                maskedValue += cleanValue[valueIndex];
                valueIndex++;
            } else {
                break;
            }
        } else if (maskChar === 'A' || maskChar === 'a') {
            // Apenas letras
            if (/[A-Za-z]/.test(cleanValue[valueIndex])) {
                maskedValue += maskChar === 'A' ? cleanValue[valueIndex].toUpperCase() : cleanValue[valueIndex].toLowerCase();
                valueIndex++;
            } else {
                break;
            }
        } else if (maskChar === '*') {
            // Qualquer caractere alfanumérico
            if (/[0-9A-Za-z]/.test(cleanValue[valueIndex])) {
                maskedValue += cleanValue[valueIndex];
                valueIndex++;
            } else {
                break;
            }
        } else {
            // Caractere literal da máscara
            maskedValue += maskChar;
        }
    }

    return maskedValue;
};

// Funções utilitárias para validação de máscaras
const validateMaskedValue = (value: string, mask: string, fieldType: string): { valid: boolean; error?: string } => {
    if (!mask || !value) {
        return { valid: true }; // Sem máscara ou valor vazio, não validar
    }

    // Remover caracteres de formatação da máscara para comparação
    const maskPattern = mask.replace(/[^9Aa*]/g, '');
    const valueClean = value.replace(/[^0-9A-Za-z]/g, '');

    // Validar comprimento mínimo baseado na máscara
    const minLength = maskPattern.length;
    if (valueClean.length < minLength) {
        return {
            valid: false,
            error: `O valor deve ter pelo menos ${minLength} caracteres conforme a máscara ${mask}`
        };
    }

    // Validações específicas por tipo
    if (fieldType === 'Número' || fieldType === 'Numérico' || fieldType === 'Decimal') {
        // Para campos numéricos, verificar se contém apenas números
        if (!/^\d+$/.test(valueClean)) {
            return { valid: false, error: 'Este campo aceita apenas números' };
        }
    }

    // Validar formato específico de máscaras conhecidas
    if (mask.includes('999.999.999-99')) {
        // CPF
        if (valueClean.length !== 11) {
            return { valid: false, error: 'CPF deve ter 11 dígitos' };
        }
    } else if (mask.includes('99.999.999/9999-99')) {
        // CNPJ
        if (valueClean.length !== 14) {
            return { valid: false, error: 'CNPJ deve ter 14 dígitos' };
        }
    } else if (mask.includes('(99)')) {
        // Telefone
        const phoneLength = valueClean.length;
        if (phoneLength !== 10 && phoneLength !== 11) {
            return { valid: false, error: 'Telefone deve ter 10 ou 11 dígitos' };
        }
    } else if (mask.includes('99999-999')) {
        // CEP
        if (valueClean.length !== 8) {
            return { valid: false, error: 'CEP deve ter 8 dígitos' };
        }
    }

    return { valid: true };
};


const MenuAtivosCampos: React.FC = () => {
    const httpClient = useHttpClient();
    const alert = useAlert();
    const { auth } = useAuth();
    const { selectedOrganizations, isInitialized, selectedCorporation, availableCorporations, availableOrganizations } = useFiltrosAtivos();
    const isVwco = availableCorporations.find(c => c.id === selectedCorporation)?.name === "VWCO";
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [availableAtivos, setAvailableAtivos] = useState<Array<{ label: string; value: string }>>([]);
    const [listaCompletaAtivos, setListaCompletaAtivos] = useState<any[]>([]);
    const [selectedAtivo, setSelectedAtivo] = useState<string | null>(null);
    const [loadingAtivos, setLoadingAtivos] = useState(false);
    const [availableCampos, setAvailableCampos] = useState<Array<{ label: string; value: string }>>([]);
    const [selectedCampo, setSelectedCampo] = useState<string | null>(null);
    const [loadingCampos, setLoadingCampos] = useState(false);
    const dataSetNamePBI = getDataSetNamePBI();
    const [sidebarTab, setSidebarTab] = useState<'movimentacoes' | 'documentos'>('movimentacoes');

    // Estados para associações de ativos
    const [associacoes, setAssociacoes] = useState<AssociacaoAtivo[]>([]);
    const [loadingAssociacoes, setLoadingAssociacoes] = useState(false);
    const [linhas, setLinhas] = useState<number>(10);
    const [offset, setOffset] = useState<number>(0);
    const [totalAssociacoes, setTotalAssociacoes] = useState<number>(0);
    const tableRef = useRef<HTMLDivElement | null>(null);
    const tabulatorRef = useRef<Tabulator | null>(null);
    const tabulatorStyleRef = useRef<HTMLStyleElement | null>(null);
    const lastOrganizacaoBuscadaRef = useRef<string | null>(null);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [savingAssociacao, setSavingAssociacao] = useState(false);
    const [novaAssociacao, setNovaAssociacao] = useState<NovaAssociacaoPayload>({
        Corporacao: '',
        Organizacao: '',
        Ativo: '',
        ItemAssociado: '',
        CampoChave: ''
    });

    const [addModalLabels, setAddModalLabels] = useState<Record<string, string>>({});

    
    const [associacoesRefreshKey, setAssociacoesRefreshKey] = useState(0);
    const [camposChave, setCamposChave] = useState<Array<{ label: string; value: string }>>([]);
    const [loadingCamposChave, setLoadingCamposChave] = useState(false);
    // Ativos disponíveis para o modal de adicionar associação
    const [availableAtivosModal, setAvailableAtivosModal] = useState<Array<{ label: string; value: string }>>([]);
    const [loadingAtivosModal, setLoadingAtivosModal] = useState(false);
    // Campos não chave (IsKeyColumn = 0) para uso no modal de edição de detalhes
    const [camposNaoChave, setCamposNaoChave] = useState<Array<{ label: string; value: string }>>([]);
    const [loadingCamposNaoChave, setLoadingCamposNaoChave] = useState(false);
    // Informações completas dos campos (incluindo máscara, tipo, etc.)
    const [camposInfo, setCamposInfo] = useState<Map<string, ICampoAssociacaoAPI>>(new Map());
    const [openDesassociarModal, setOpenDesassociarModal] = useState(false);
    const [associacaoParaDesassociar, setAssociacaoParaDesassociar] = useState<AssociacaoAtivo | null>(null);
    const [desassociando, setDesassociando] = useState(false);

    // Helpers para data e hora padrão (hoje / horário atual)
    const getTodayISODate = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getCurrentTimeHM = () => {
        const d = new Date();
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // Debug: monitorar mudanças no estado de associações
    useEffect(() => {
        console.log('🔄 Estado associacoes mudou:', associacoes.length, 'itens');
        if (associacoes.length > 0) {
            console.log('📋 Primeira associação no estado:', associacoes[0]);
        }
    }, [associacoes]);

    const [openModal, setOpenModal] = useState(false);
    const [associacaoEditando, setAssociacaoEditando] = useState<AssociacaoAtivo | null>(null);
    const [updatingDetalhes, setUpdatingDetalhes] = useState(false);
    const [loadingDetalhesExistentes, setLoadingDetalhesExistentes] = useState(false);
    // FormData dinâmico: chave = LabelKey do campo, valor = valor digitado pelo usuário
    const [formData, setFormData] = useState<FormDataDetalhes>({});
    // Rastrear quais campos já existem (para fazer PUT ao invés de POST)
    const [camposExistentes, setCamposExistentes] = useState<Set<string>>(new Set());
    // Estado para controlar a aba ativa
    const [activeTab, setActiveTab] = useState(0);
    // Estados para sidebar de movimentações
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [selectedHistorico, setSelectedHistorico] = useState<HistoricoAtivo | null>(null);
    const [movimentacoes, setMovimentacoes] = useState<IMovimentacaoHistorico[]>([]);
    const [loadingMovimentacoes, setLoadingMovimentacoes] = useState(false);
    const movimentacoesTableRef = useRef<HTMLDivElement | null>(null);
    const movimentacoesTabulatorRef = useRef<Tabulator | null>(null);

    // Estados para sidebar de filtros do histórico
    const [filtrosSidebarOpen, setFiltrosSidebarOpen] = useState(false);
    const [camposFiltro, setCamposFiltro] = useState<ICampoAssociacaoAPI[]>([]);
    const [loadingCamposFiltro, setLoadingCamposFiltro] = useState(false);
    const [filtrosAplicados, setFiltrosAplicados] = useState<Record<string, any>>({});
    const [historicoFiltrado, setHistoricoFiltrado] = useState<HistoricoAtivo[]>([]);

    // Estados para filtro histórico baseado na API de campos
    const [camposDisponiveisFiltro, setCamposDisponiveisFiltro] = useState<ICampoAssociacaoAPI[]>([]);
    const [loadingCamposFiltroHistorico, setLoadingCamposFiltroHistorico] = useState(false);
    const [campoSelecionadoFiltro, setCampoSelecionadoFiltro] = useState<string>('');
    const [campoSelecionadoInfo, setCampoSelecionadoInfo] = useState<ICampoAssociacaoAPI | null>(null);
    const [valorDigitadoFiltro, setValorDigitadoFiltro] = useState<string>('');
    const [erroValidacaoFiltro, setErroValidacaoFiltro] = useState<string>('');

// Mapeamento de labels para o modal de adicionar associação, baseado nos campos disponíveis
    const getAddModalLabel = useCallback((fieldKey: string, defaultLabel: string) => {
        const foundKey = Object.keys(addModalLabels).find(k => k.toLowerCase() === fieldKey.toLowerCase());
        return foundKey ? addModalLabels[foundKey] : defaultLabel;
    }, [addModalLabels]);


    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        if (tabulatorStyleRef.current) return;

        const styleElement = document.createElement('style');
        styleElement.id = 'menu-ativos-campos-tabulator-styles';
        styleElement.innerHTML = `
            .tabulator .tabulator-header .tabulator-col .tabulator-header-filter input {
                border-radius: 4px;
                border: 1px solid #c4c4c4;
                padding: 6px 8px;
                font-size: 0.85rem;
            }
                .coluna-acoes-bottom .tabulator-col-content {
                display: flex !important;
                flex-direction: column;
                justify-content: flex-start !important; /* Joga o conteúdo para o TOPO */
                height: 100%;
            }
            .tabulator .tabulator-row {
                min-height: 44px;
            }
            .tabulator .tabulator-cell {
                font-size: 0.9rem;
            }
            .tabulator-action-buttons {
                display: flex;
                justify-content: center;
                gap: 8px;
            }
            .tabulator-action-button {
                border: none;
                border-radius: 4px;
                padding: 4px 10px;
                font-size: 0.75rem;
                font-weight: 600;
                cursor: pointer;
                color: #fff;
                background-color: #1976d2;
                transition: opacity 0.2s ease;
            }
            .tabulator-action-button.secondary {
                background-color: #2e7d32;
            }
            .tabulator-action-button:disabled,
            .tabulator-action-button[disabled] {
                opacity: 0.4;
                cursor: not-allowed;
            }
        `;
        document.head.appendChild(styleElement);
        tabulatorStyleRef.current = styleElement;

        return () => {
            if (styleElement.parentNode) {
                styleElement.parentNode.removeChild(styleElement);
            }
            tabulatorStyleRef.current = null;
        };
    }, []);


    useEffect(() => {
        // Se houver organizações selecionadas no filtro global, usar a primeira
        if (isInitialized && selectedOrganizations.length > 0 && availableOrganizations && availableOrganizations.length > 0) {
            const org = availableOrganizations.find(o => selectedOrganizations.includes(o.OrganizationName));
            if (org) {
                setSelectedOrganizationId(org.OrganizationId);
            }
        }
    }, [selectedOrganizations, availableOrganizations, isInitialized]);

    // Buscar ativos quando organizações mudarem
    useEffect(() => {
        if (isInitialized && selectedOrganizations.length > 0) {
            fetchAtivos(selectedOrganizations);
        } else {
            setAvailableAtivos([]);
            setSelectedAtivo(null);
        }
    }, [selectedOrganizations, isInitialized]);

    // Buscar campos quando corporação ou organização mudarem
    useEffect(() => {
        if (isInitialized && selectedCorporation && availableCorporations.length > 0 && selectedOrganizations.length > 0 && availableOrganizations) {
            const corporacao = availableCorporations.find(c => c.id === selectedCorporation);
            const organizacao = availableOrganizations.find(o => selectedOrganizations.includes(o.OrganizationName));
            if (corporacao && organizacao) {
                fetchCampos(corporacao.name, organizacao.OrganizationName);
            }
        } else {
            setAvailableCampos([]);
            setSelectedCampo(null);
        }
    }, [selectedCorporation, selectedOrganizations, isInitialized, availableCorporations, availableOrganizations]);

    const fetchOrganizations = async () => {
        setLoading(true);
        try {
            const response = await httpClient.request({
                method: RequestMethod.GET,
                endpoint: '/api/Organization',
            });

            if (response.success && response.payload) {
                const orgs = Array.isArray(response.payload) ? response.payload : [];
                setOrganizations(orgs);

                // Se houver apenas uma organização, selecionar automaticamente
                if (orgs.length === 1) {
                    setSelectedOrganizationId(orgs[0].Id);
                }
            }
        } catch (error) {
            console.error('Erro ao buscar organizações:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: 'Erro ao carregar organizações',
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAtivos = async (orgs: string[]) => {
        setLoadingAtivos(true);
        try {
            const endpoint = '/Gerenciamento/PowerBI/ObterAtivosByOrg';
            const queryParams = new URLSearchParams();

            if (dataSetNamePBI) {
                queryParams.append('datasetName', dataSetNamePBI);
            }

            if (orgs && orgs.length > 0) {
                orgs.forEach(org => {
                    queryParams.append('organization', org.trim());
                });
            }

            const queryString = queryParams.toString();
            const fullUrl = `${endpoint}?${queryString}`;

            const response = await httpClient.request({
                method: RequestMethod.GET,
                endpoint: fullUrl,
            });

            if (response.success && response.payload) {
                setListaCompletaAtivos(Array.isArray(response.payload) ? response.payload : []);
                const uniqueAtivos = Array.from(
                    new Set(response.payload.map((item: any) => item.Ativo))
                ).filter((ativo): ativo is string =>
                    typeof ativo === 'string' && ativo !== ''
                ).sort();

                const ativosOptions = uniqueAtivos.map(ativo => ({
                    label: ativo,
                    value: ativo
                }));

                setAvailableAtivos(ativosOptions);
            }
        } catch (error) {
            console.error('Erro ao buscar ativos:', error);
            setAvailableAtivos([]);
            setListaCompletaAtivos([]);
        } finally {
            setLoadingAtivos(false);
        }
    };

    const handleAtivoChange = (selectedOption: any) => {
        const ativoValue = selectedOption ? selectedOption.value : null;
        setSelectedAtivo(ativoValue);
        setOffset(0); // Resetar paginação ao mudar o ativo
    };
    // Criar chaves únicas para identificar mudanças nas dependências de array
    const organizacaoKey = selectedOrganizations.join(',');
    const corporacaoKey = selectedCorporation?.toString() || '';
    const canDisplayAssociacoes = isInitialized && selectedOrganizations.length > 0 && !!selectedCorporation;
    const selectedCorporationName = useMemo(() => {
        if (!selectedCorporation || availableCorporations.length === 0) return '';
        const corp = availableCorporations.find(c => c.id === selectedCorporation);
        return corp ? corp.name : '';
    }, [selectedCorporation, availableCorporations]);
    const selectedOrganizationName = selectedOrganizations[0] || '';

    const fetchMovimentacoesHistorico = useCallback(async (historico: HistoricoAtivo) => {
        if (!backendUrl || !auth?.Token || !selectedCorporationName || !selectedOrganizationName) {
            console.warn('⚠️ [MOVIMENTAÇÕES] Dados insuficientes para buscar movimentações');
            return;
        }

        setLoadingMovimentacoes(true);
        try {
            const encodedCorporacao = encodeURIComponent(selectedCorporationName.trim());
            const encodedOrganizacao = encodeURIComponent(selectedOrganizationName.trim());
            const endpoint = `/api/trackingAdjusted/${encodedCorporacao}/${encodedOrganizacao}/TrackingAdjusted`;

            const requestBody = {
                Ativos: [historico.ativo],
                Linhas: 100,
                OffSet: 0
            };

            console.log('🕐 [MOVIMENTAÇÕES] Buscando movimentações para ativo:', historico.ativo);

            const fullUrl = `${backendUrl}${endpoint}`;
            console.log('🔵 [MOVIMENTAÇÕES] Fazendo requisição POST para:', fullUrl);
            console.log('📦 [MOVIMENTAÇÕES] Body da requisição:', JSON.stringify(requestBody, null, 2));

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                },
                body: JSON.stringify(requestBody)
            });

            console.log('📊 [MOVIMENTAÇÕES] Status da resposta:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ [MOVIMENTAÇÕES] Erro na requisição:', response.status, errorText);
                throw new Error(errorText || 'Erro ao buscar movimentações');
            }

            const data: IMovimentacaoHistorico[] = await response.json();
            console.log('✅ [MOVIMENTAÇÕES] Dados recebidos:', data.length, 'itens');
            console.log('📋 [MOVIMENTAÇÕES] Primeiro item:', data[0]);

            // Filtrar apenas movimentações do item associado (veículo) do histórico
            const movimentacoesFiltradas = data.filter(item =>
                item.ItemAssociado === historico.veiculo
            );
            console.log('✅ [MOVIMENTAÇÕES] Movimentações filtradas por veículo:', movimentacoesFiltradas.length, 'itens');

            setMovimentacoes(movimentacoesFiltradas);
        } catch (error: any) {
            console.error('❌ [MOVIMENTAÇÕES] Erro ao buscar movimentações:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: error?.message || 'Erro ao carregar movimentações do histórico.'
            });
            setMovimentacoes([]);
        } finally {
            setLoadingMovimentacoes(false);
        }
    }, [backendUrl, auth?.Token, selectedCorporationName, selectedOrganizationName, alert]);

    const handleHistoricoRowClick = useCallback((historico: HistoricoAtivo) => {
        console.log('📍 [HISTÓRICO] Linha clicada:', historico);
        setSelectedHistorico(historico);
        setSidebarTab('movimentacoes');
        setSidebarVisible(true);
        fetchMovimentacoesHistorico(historico);
    }, [fetchMovimentacoesHistorico]);


    const fetchCampos = async (corporationName: string, organizationName: string) => {
        if (!corporationName || !organizationName) return;

        setLoadingCampos(true);
        try {
            const encodedCorporacao = encodeURIComponent(corporationName);
            const encodedOrganizacao = encodeURIComponent(organizationName);
            // Usar o mesmo formato do endpoint de associações
            const endpoint = `/api/associacoes/${encodedCorporacao}/${encodedOrganizacao}/Associacoes/ativas`;

            console.log('🔵 Buscando campos via POST:', endpoint);

            const response = await httpClient.request({
                method: RequestMethod.POST,
                endpoint: endpoint,
                data: {
                    // Body vazio ou conforme necessário pela API
                }
            });

            console.log('✅ Resposta da API de campos:', response);


            const data = response.payload;

            // A resposta pode estar diretamente como array ou dentro de um objeto
            let apiFields: ICampoAssociacaoAPI[] = [];

            if (Array.isArray(data)) {
                apiFields = data;
            } else if (data && typeof data === 'object') {
                const possibleArrays = Object.values(data).filter(Array.isArray);
                if (possibleArrays.length > 0) {
                    apiFields = possibleArrays[0] as ICampoAssociacaoAPI[];
                }
            }

            // Criar opções para o filtro usando LabelKey e LabelValue
            const camposOptions = apiFields.map((campo) => ({
                label: `${campo.LabelKey} - ${campo.LabelValue}`,
                value: campo.LabelKey
            })).sort((a, b) => a.label.localeCompare(b.label));

            setAvailableCampos(camposOptions);
        } catch (error) {
            console.error('Erro ao buscar campos:', error);
            setAvailableCampos([]);
        } finally {
            setLoadingCampos(false);
        }
    };

    const handleCampoChange = (selectedOption: any) => {
        const campoValue = selectedOption ? selectedOption.value : null;
        setSelectedCampo(campoValue);
        setOffset(0); // Resetar paginação ao mudar o campo
    };



    // Buscar associações quando organização, corporação, ativo, campo, linhas ou offset mudarem
    useEffect(() => {
        console.log('🟡 useEffect de associações disparado', {
            isInitialized,
            selectedOrganizations: selectedOrganizations.length,
            selectedCorporation,
            availableCorporations: availableCorporations.length,
            organizacaoKey,
            corporacaoKey,
            selectedAtivo,
            selectedCampo,
            linhas,
            offset
        });

        // Verificar se temos todas as condições necessárias
        if (!isInitialized) {
            console.log('❌ Condição não atendida: isInitialized = false');
            setAssociacoes([]);
            setTotalAssociacoes(0);
            return;
        }

        if (selectedOrganizations.length === 0) {
            console.log('❌ Condição não atendida: selectedOrganizations está vazio');
            setAssociacoes([]);
            setTotalAssociacoes(0);
            return;
        }

        if (!selectedCorporation) {
            console.log('❌ Condição não atendida: selectedCorporation é null/undefined');
            setAssociacoes([]);
            setTotalAssociacoes(0);
            return;
        }

        if (availableCorporations.length === 0) {
            console.log('❌ Condição não atendida: availableCorporations está vazio');
            setAssociacoes([]);
            setTotalAssociacoes(0);
            return;
        }

        if (!availableOrganizations || availableOrganizations.length === 0) {
            console.log('⚠️ availableOrganizations ainda está vazio, aguardando carregamento...');
            setAssociacoes([]);
            setTotalAssociacoes(0);
            return;
        }

        console.log('✅ Todas as condições atendidas, iniciando busca de associações');

        const fetchAssociacoes = async () => {
            setLoadingAssociacoes(true);
            try {
                const corporacao = availableCorporations.find(c => c.id === selectedCorporation);
                const organizacao = availableOrganizations.find(o => selectedOrganizations.includes(o.OrganizationName));

                console.log('🔍 Buscando dados:', {
                    corporacao: corporacao ? corporacao.name : 'não encontrada',
                    organizacao: organizacao ? organizacao.OrganizationName : 'não encontrada',
                    availableOrganizationsLength: availableOrganizations.length,
                    selectedOrganizations: selectedOrganizations,
                    availableCorporations: availableCorporations.map(c => ({ id: c.id, name: c.name })),
                    availableOrganizations: availableOrganizations.map(o => o.OrganizationName)
                });

                if (!corporacao) {
                    console.log('❌ Corporação não encontrada. ID buscado:', selectedCorporation);
                    setAssociacoes([]);
                    setTotalAssociacoes(0);
                    return;
                }

                if (!organizacao) {
                    console.log('❌ Organização não encontrada. Organizações buscadas:', selectedOrganizations);
                    console.log('📋 Organizações disponíveis:', availableOrganizations.map(o => o.OrganizationName));
                    setAssociacoes([]);
                    setTotalAssociacoes(0);
                    return;
                }

                const encodedCorporacao = encodeURIComponent(corporacao.name);
                const encodedOrganizacao = encodeURIComponent(organizacao.OrganizationName);
                const endpoint = `/api/associacoes/${encodedCorporacao}/${encodedOrganizacao}/Associacoes/ativas`;

                // Preparar arrays de filtros - usar [""] quando não houver seleção
                const ativosArray = selectedAtivo ? [selectedAtivo] : [""];
                const itensAssociadosArray = selectedCampo ? [selectedCampo] : [""];

                const requestBody = {
                    Ativos: ativosArray,
                    ItensAssociados: itensAssociadosArray,
                    Linhas: linhas,
                    Offset: offset
                };

                const fullUrl = `${backendUrl}${endpoint}`;
                console.log('🔵 Fazendo requisição POST para:', fullUrl);
                console.log('📦 Body da requisição:', JSON.stringify(requestBody, null, 2));

                // Usar fetch diretamente pois a API retorna array diretamente, não no formato esperado pelo httpClient
                const response = await fetch(fullUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${auth?.Token}`
                    },
                    body: JSON.stringify(requestBody)
                });

                console.log('📊 Status da resposta:', response.status, response.statusText);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('❌ Erro na requisição:', response.status, errorText);
                    setAssociacoes([]);
                    setTotalAssociacoes(0);
                    return;
                }

                const associacoesData: any[] = await response.json();
                console.log('✅ Dados recebidos da API:', associacoesData.length, 'itens');
                console.log('📋 Primeiro item:', associacoesData[0]);

                if (Array.isArray(associacoesData) && associacoesData.length > 0) {
                    // Mapear os dados da API para o formato esperado
                    // Formato da API: { Ativo, ItemAssociado, DataAssociacao, DataDesassociacao, Tempo }
                    const associacoesMapeadas: AssociacaoAtivo[] = associacoesData.map((item: any, index: number) => {
                        // Converter Tempo (formato "dd.HH:mm:ss") para minutos
                        const calcularMinutos = (tempo: string): number => {
                            if (!tempo) return 0;
                            try {
                                const partes = tempo.split('.');
                                if (partes.length === 2) {
                                    const [dias, tempoRestante] = partes;
                                    const [horas, minutos, segundos] = tempoRestante.split(':').map(Number);
                                    const totalMinutos = (parseInt(dias) * 24 * 60) + (horas * 60) + minutos + (segundos / 60);
                                    return Math.round(totalMinutos);
                                }
                            } catch (e) {
                                console.warn('Erro ao calcular minutos do tempo:', tempo);
                            }
                            return 0;
                        };

                        return {
                            id: index + 1,
                            ativo: item.Ativo || '',
                            veiculo: item.ItemAssociado || '',
                            associadoEm: item.DataAssociacao || '',
                            desassociadoEm: item.DataDesassociacao || null,
                            duracaoMinutos: calcularMinutos(item.Tempo || ''),
                            tempo: item.Tempo || ''
                        };
                    });

                    console.log('✅ Associações mapeadas:', associacoesMapeadas.length, 'itens');
                    console.log('📋 Primeira associação mapeada:', associacoesMapeadas[0]);

                    setAssociacoes(associacoesMapeadas);
                    console.log('✅ Estado associacoes atualizado com', associacoesMapeadas.length, 'itens');

                    // Definir total de associações para paginação
                    // Se retornou menos itens que o solicitado, chegamos ao fim
                    if (associacoesMapeadas.length < linhas) {
                        setTotalAssociacoes(offset + associacoesMapeadas.length);
                    } else {
                        // Se retornou exatamente o número solicitado, pode haver mais páginas
                        setTotalAssociacoes(offset + linhas + 1);
                    }

                    console.log('📊 Total de associações definido:', offset + associacoesMapeadas.length);
                } else {
                    console.warn('⚠️ Nenhum dado retornado ou array vazio');
                    setAssociacoes([]);
                    setTotalAssociacoes(0);
                }
            } catch (error) {
                console.error('Erro ao buscar associações:', error);
                alert.createMessage({
                    icon: 'error',
                    title: 'Erro',
                    html: 'Erro ao carregar associações de ativos',
                });
                setAssociacoes([]);
                setTotalAssociacoes(0);
            } finally {
                setLoadingAssociacoes(false);
            }
        };

        fetchAssociacoes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAtivo, selectedCampo, linhas, offset, organizacaoKey, corporacaoKey, isInitialized, availableOrganizations?.length, associacoesRefreshKey]);



    const handleLinhasChange = (event: any) => {
        const novasLinhas = parseInt(event.target.value, 10);
        setLinhas(novasLinhas);
        setOffset(0); // Resetar para primeira página ao mudar o número de linhas
    };

    const handlePaginaAnterior = () => {
        if (offset > 0) {
            setOffset(Math.max(0, offset - linhas));
        }
    };

    const handleProximaPagina = () => {
        // Se temos total, usar para verificar
        if (totalAssociacoes > 0) {
            if (offset + linhas < totalAssociacoes) {
                setOffset(offset + linhas);
            }
        } else {
            // Se não temos total, verificar se retornou menos itens que o solicitado
            // Se retornou exatamente o número de linhas, assumir que pode haver mais
            setOffset(offset + linhas);
        }
    };

    const getPaginaAtual = () => {
        return Math.floor(offset / linhas) + 1;
    };

    const getTotalPaginas = () => {
        return Math.ceil(totalAssociacoes / linhas);
    };

    const handleEditAssociacao = useCallback((associacao: AssociacaoAtivo) => {
        setAssociacaoEditando(associacao);
        // Inicializar formData vazio - será preenchido quando os campos não chave forem carregados
        setFormData({});
        setOpenModal(true);
    }, []);

    const handleAssociar = useCallback((associacao: AssociacaoAtivo) => {
        // Renomeado: na verdade é desassociar
        setAssociacaoParaDesassociar(associacao);
        setOpenDesassociarModal(true);
    }, []);

    const handleCloseDesassociarModal = useCallback(() => {
        setOpenDesassociarModal(false);
        setAssociacaoParaDesassociar(null);
    }, []);

    const handleConfirmarDesassociar = useCallback(async () => {
        if (!associacaoParaDesassociar || !backendUrl || !auth?.Token) {
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: 'Dados insuficientes para desassociar.'
            });
            return;
        }

        // Buscar informações da corporação e organização atual
        const corporacaoNome = selectedCorporationName;
        const organizacaoNome = selectedOrganizationName;
        const ativo = associacaoParaDesassociar.ativo;

        if (!corporacaoNome || !organizacaoNome || !ativo) {
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: 'Corporação, Organização ou Ativo não encontrados.'
            });
            return;
        }

        setDesassociando(true);
        try {
            const encodedCorporacao = encodeURIComponent(corporacaoNome.trim());
            const encodedOrganizacao = encodeURIComponent(organizacaoNome.trim());
            const encodedAtivo = encodeURIComponent(ativo.trim());
            const endpoint = `/api/associacoes/${encodedCorporacao}/${encodedOrganizacao}/Associacoes/${encodedAtivo}`;
            const fullUrl = `${backendUrl}${endpoint}`;

            console.log('🔴 [DESASSOCIAR] Fazendo requisição DELETE para:', fullUrl);
            console.log('🔴 [DESASSOCIAR] Parâmetros:', {
                corporacao: corporacaoNome,
                organizacao: organizacaoNome,
                ativo: ativo
            });

            const response = await fetch(fullUrl, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                }
            });

            console.log('📊 [DESASSOCIAR] Status da resposta:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ [DESASSOCIAR] Erro na resposta:', response.status, errorText);
                throw new Error(errorText || 'Erro ao desassociar');
            }

            alert.createMessage({
                icon: 'success',
                title: 'Desassociação realizada',
                html: `O ativo ${associacaoParaDesassociar.ativo} foi desassociado com sucesso.`
            });

            handleCloseDesassociarModal();
            setAssociacoesRefreshKey((prev) => prev + 1);
        } catch (error: any) {
            console.error('❌ [DESASSOCIAR] Erro ao desassociar:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: error?.message || 'Erro ao desassociar ativo.'
            });
        } finally {
            setDesassociando(false);
        }
    }, [associacaoParaDesassociar, backendUrl, auth?.Token, selectedCorporationName, selectedOrganizationName, alert, handleCloseDesassociarModal]);

    const handleCloseModal = useCallback(() => {
        setOpenModal(false);
        setAssociacaoEditando(null);
        setFormData({});
        setCamposExistentes(new Set());
    }, []);

    // Função para buscar detalhes existentes via GET
    const fetchDetalhesExistentes = useCallback(async (
        corporacaoNome: string,
        organizacaoNome: string,
        ativo: string
    ): Promise<Array<{ Campo: string; Descrição: string; Valor: string }>> => {
        if (!backendUrl || !auth?.Token || !corporacaoNome || !organizacaoNome || !ativo) {
            console.warn('⚠️ [DETALHES EXISTENTES] Validação falhou');
            return [];
        }

        const encodedCorporacao = encodeURIComponent(corporacaoNome.trim());
        const encodedOrganizacao = encodeURIComponent(organizacaoNome.trim());
        const encodedAtivo = encodeURIComponent(ativo.trim());
        const endpoint = `/api/associacoes/detalhes/${encodedCorporacao}/${encodedOrganizacao}/AssociacoesDetalhes?ativo=${encodedAtivo}`;
        const fullUrl = `${backendUrl}${endpoint}`;

        console.log('🔵 [DETALHES EXISTENTES] Fazendo requisição GET para:', fullUrl);

        try {
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                }
            });

            console.log('📊 [DETALHES EXISTENTES] Status da resposta:', response.status, response.statusText);

            if (!response.ok) {
                // Se for 404, não há detalhes ainda, retornar array vazio
                if (response.status === 404) {
                    console.log('ℹ️ [DETALHES EXISTENTES] Nenhum detalhe encontrado (404)');
                    return [];
                }
                const errorText = await response.text();
                console.error('❌ [DETALHES EXISTENTES] Erro na resposta:', response.status, errorText);
                throw new Error(errorText || 'Erro ao buscar detalhes existentes');
            }

            let data = await response.json();
            console.log('✅ [DETALHES EXISTENTES] Dados recebidos:', data);

            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch (e) {
                    console.error('❌ [DETALHES EXISTENTES] Erro no parse:', e);
                    return [];
                }
            }

            if (!Array.isArray(data)) {
                console.warn('⚠️ [DETALHES EXISTENTES] Dados não são um array:', typeof data);
                return [];
            }

            return data;
        } catch (error: any) {
            console.error('❌ [DETALHES EXISTENTES] Erro na busca:', error);
            // Não lançar erro, apenas retornar array vazio para não quebrar o fluxo
            return [];
        }
    }, [auth?.Token, backendUrl]);

    const handleUpdate = useCallback(async () => {
        if (!associacaoEditando) {
            return;
        }

        // Validar se há pelo menos um campo preenchido
        const camposPreenchidos = Object.entries(formData).filter(([_, value]) => value && value.trim() !== '');

        if (camposPreenchidos.length === 0) {
            alert.createMessage({
                icon: 'warning',
                title: 'Atenção',
                html: 'Preencha pelo menos um campo para atualizar.'
            });
            return;
        }

        // Validar campos conforme máscaras
        const errosValidacao: string[] = [];
        camposPreenchidos.forEach(([labelKey, value]) => {
            const campoInfo = camposInfo.get(labelKey);
            if (campoInfo) {
                const mask = campoInfo.LabelMask || '';
                const fieldType = campoInfo.LabelType || '';
                const validation = validateMaskedValue(value.trim(), mask, fieldType);

                if (!validation.valid && validation.error) {
                    const campoLabel = campoInfo.LabelValue || labelKey;
                    errosValidacao.push(`${campoLabel}: ${validation.error}`);
                }
            }
        });

        if (errosValidacao.length > 0) {
            alert.createMessage({
                icon: 'error',
                title: 'Erro de Validação',
                html: `Os seguintes campos contêm erros:<br/>${errosValidacao.map(e => `• ${e}`).join('<br/>')}`
            });
            return;
        }

        // Validar se temos os dados necessários
        if (!backendUrl || !auth?.Token) {
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: 'Não foi possível identificar o backend ou usuário autenticado.'
            });
            return;
        }

        const corporacaoNome = selectedCorporationName;
        const organizacaoNome = selectedOrganizationName;
        const ativo = associacaoEditando.ativo;

        if (!corporacaoNome || !organizacaoNome || !ativo) {
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: 'Corporação, Organização ou Ativo não encontrados.'
            });
            return;
        }

        setUpdatingDetalhes(true);

        try {
            const encodedCorporacao = encodeURIComponent(corporacaoNome.trim());
            const encodedOrganizacao = encodeURIComponent(organizacaoNome.trim());
            const endpoint = `/api/associacoes/detalhes/${encodedCorporacao}/${encodedOrganizacao}/AssociacoesDetalhes`;
            const fullUrl = `${backendUrl}${endpoint}`;

            console.log('🔵 [DETALHES] Iniciando atualização de detalhes:', {
                corporacao: corporacaoNome,
                organizacao: organizacaoNome,
                ativo: ativo,
                camposPreenchidos: camposPreenchidos.length
            });

            // Fazer POST ou PUT para cada campo preenchido
            // campoKey é o LabelKey do campo não chave, valor é o que o usuário digitou
            const promises = camposPreenchidos.map(async ([campoKey, valor]) => {
                const payload = {
                    Ativo: ativo,
                    Campo: campoKey, // LabelKey do campo não chave
                    Valor: valor.trim() // Valor digitado pelo usuário
                };

                // Verificar se o campo já existe para decidir entre POST e PUT
                const campoJaExiste = camposExistentes.has(campoKey);
                const method = campoJaExiste ? 'PUT' : 'POST';

                console.log(`📤 [DETALHES] Enviando ${method} para campo "${campoKey}":`, payload);

                const response = await fetch(fullUrl, {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json-patch+json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${auth.Token}`
                    },
                    body: JSON.stringify(payload)
                });

                console.log(`📊 [DETALHES] Resposta para campo "${campoKey}":`, response.status, response.statusText);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`❌ [DETALHES] Erro ao ${method === 'PUT' ? 'atualizar' : 'criar'} campo "${campoKey}":`, response.status, errorText);
                    throw new Error(`Erro ao ${method === 'PUT' ? 'atualizar' : 'criar'} campo "${campoKey}": ${errorText || response.statusText}`);
                }

                // Se foi POST bem-sucedido, adicionar ao conjunto de campos existentes
                if (method === 'POST') {
                    setCamposExistentes((prev) => new Set(prev).add(campoKey));
                }

                return { campo: campoKey, sucesso: true, method };
            });

            // Aguardar todas as requisições
            const resultados = await Promise.allSettled(promises);

            // Verificar se houve erros
            const erros = resultados.filter(r => r.status === 'rejected');
            const sucessos = resultados.filter(r => r.status === 'fulfilled');

            if (erros.length > 0) {
                const mensagensErro = erros.map((e: any) => e.reason?.message || 'Erro desconhecido').join('; ');
                console.error('❌ [DETALHES] Erros encontrados:', erros);
                alert.createMessage({
                    icon: 'error',
                    title: 'Erro ao atualizar',
                    html: `${sucessos.length} campo(s) atualizado(s) com sucesso. ${erros.length} campo(s) falharam: ${mensagensErro}`
                });
            } else {
                console.log('✅ [DETALHES] Todos os campos foram atualizados com sucesso');
                alert.createMessage({
                    icon: 'success',
                    title: 'Sucesso',
                    html: `${sucessos.length} campo(s) atualizado(s) com sucesso!`
                });
                handleCloseModal();
                // Atualizar a lista de associações
                setAssociacoesRefreshKey((prev) => prev + 1);
            }
        } catch (error: any) {
            console.error('❌ [DETALHES] Erro geral ao atualizar detalhes:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: error?.message || 'Erro ao atualizar informações adicionais.'
            });
        } finally {
            setUpdatingDetalhes(false);
        }
    }, [associacaoEditando, formData, camposExistentes, backendUrl, auth?.Token, selectedCorporationName, selectedOrganizationName, alert, handleCloseModal]);

    const formatarDuracao = useCallback((minutos: number): string => {
        return `${minutos} min`;
    }, []);

    const formatarTempo = useCallback((tempo: string): string => {
        if (!tempo) return '-';
        // Formato: "dd.HH:mm:ss" -> "dd dias, HH:mm:ss"
        try {
            const partes = tempo.split('.');
            if (partes.length === 2) {
                const [dias, tempoRestante] = partes;
                const diasNum = parseInt(dias);
                if (diasNum > 0) {
                    return `${diasNum} dia${diasNum > 1 ? 's' : ''}, ${tempoRestante}`;
                }
                return tempoRestante;
            }
        } catch (e) {
            console.warn('Erro ao formatar tempo:', tempo);
        }
        return tempo;
    }, []);

    const tabulatorHandlersRef = useRef({
        handleEditAssociacao,
        handleAssociar,
        formatarTempo,
        formatarDuracao,
        handleHistoricoRowClick
    });
    const lastCamposChaveCorpRef = useRef<string | null>(null);

    useEffect(() => {
        tabulatorHandlersRef.current = {
            handleEditAssociacao,
            handleAssociar,
            formatarTempo,
            formatarDuracao,
            handleHistoricoRowClick
        };
    }, [handleEditAssociacao, handleAssociar, formatarTempo, formatarDuracao, handleHistoricoRowClick]);

    const handleNovaAssociacaoChange = useCallback((field: keyof NovaAssociacaoPayload, value: string) => {
        setNovaAssociacao((prev) => {
            const updated = {
                ...prev,
                [field]: value
            };

            // Limpar CampoChave e Organizacao quando Corporacao mudar
            if (field === 'Corporacao') {
                updated.CampoChave = '';
                updated.Organizacao = '';
                updated.Ativo = '';
            }

            // Limpar Ativo quando Organizacao mudar
            if (field === 'Organizacao' && prev.Ativo) {
                updated.Ativo = '';
            }

            return updated;
        });
    }, []);

    const fetchCamposChaveFromApi = useCallback(async (corporacaoNome: string) => {
        console.log('🔵 [CAMPO CHAVE] Iniciando busca de campos chave para corporação:', corporacaoNome);

        if (!backendUrl || !auth?.Token || !corporacaoNome) {
            console.warn('⚠️ [CAMPO CHAVE] Validação falhou:', {
                backendUrl: !!backendUrl,
                hasToken: !!auth?.Token,
                corporacaoNome: corporacaoNome
            });
            return [];
        }

        const encodedCorp = encodeURIComponent(corporacaoNome);
        const endpoint = `/api/associacao/campos/corporacao${encodedCorp}`;
        const fullUrl = `${backendUrl}${endpoint}`;

        console.log('🔵 [CAMPO CHAVE] Fazendo requisição GET para:', fullUrl);

        try {
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                }
            });

            console.log('📊 [CAMPO CHAVE] Status da resposta:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ [CAMPO CHAVE] Erro na resposta:', response.status, errorText);
                throw new Error(errorText || 'Erro ao buscar campos chave');
            }

            let data = await response.json();
            console.log('✅ [CAMPO CHAVE] Dados brutos recebidos:', data);

            if (typeof data === 'string') {
                console.log('🔄 [CAMPO CHAVE] Dados são string, tentando parse...');
                try {
                    data = JSON.parse(data);
                    console.log('✅ [CAMPO CHAVE] Parse bem-sucedido:', data);
                } catch (e) {
                    console.error('❌ [CAMPO CHAVE] Erro no parse:', e);
                    data = [];
                }
            }

            if (!Array.isArray(data)) {
                console.warn('⚠️ [CAMPO CHAVE] Dados não são um array:', typeof data, data);
                return [];
            }

            console.log('📋 [CAMPO CHAVE] Total de campos recebidos:', data.length);
            console.log('📋 [CAMPO CHAVE] Primeiro campo (exemplo):', data[0]);

            // Filtrar apenas campos chave
            const camposChave = data.filter((campo: any) => {
                const isKey = Number(campo?.IsKeyColumn) === 1;
                console.log('🔍 [CAMPO CHAVE] Verificando campo:', {
                    LabelKey: campo?.LabelKey,
                    LabelValue: campo?.LabelValue,
                    IsKeyColumn: campo?.IsKeyColumn,
                    isKey
                });
                return isKey;
            });

            console.log('🔑 [CAMPO CHAVE] Campos chave encontrados:', camposChave.length);
            console.log('🔑 [CAMPO CHAVE] Exemplo de campo chave:', camposChave[0]);

            // Criar opções com formato "LabelKey - LabelValue"
            const options = camposChave
                .map((campo: any) => {
                    const labelKey = campo?.LabelKey || '';
                    const labelValue = campo?.LabelValue || '';

                    // Formato: "LabelKey - LabelValue" ou apenas um dos dois se o outro não existir
                    let label = '';
                    if (labelKey && labelValue) {
                        label = `${labelKey} - ${labelValue}`;
                    } else if (labelKey) {
                        label = labelKey;
                    } else if (labelValue) {
                        label = labelValue;
                    }

                    const option = {
                        label: label,
                        value: labelKey
                    };

                    console.log('📝 [CAMPO CHAVE] Criando opção:', option);
                    return option;
                })
                .filter((opt: { label: string; value: string }) => {
                    const isValid = !!opt.value && !!opt.label;
                    if (!isValid) {
                        console.warn('⚠️ [CAMPO CHAVE] Opção inválida descartada:', opt);
                    }
                    return isValid;
                });

            console.log('✅ [CAMPO CHAVE] Total de opções finais:', options.length);
            console.log('✅ [CAMPO CHAVE] Opções finais:', options);

            return options;
        } catch (error: any) {
            console.error('❌ [CAMPO CHAVE] Erro na busca:', error);
            throw error;
        }
    }, [auth?.Token, backendUrl]);

    // Buscar campos NÃO chave (IsKeyColumn = 0) para o modal de edição
    const fetchCamposNaoChaveFromApi = useCallback(async (corporacaoNome: string) => {
        console.log('🔵 [CAMPOS NÃO CHAVE] Iniciando busca para corporação:', corporacaoNome);

        if (!backendUrl || !auth?.Token || !corporacaoNome) {
            console.warn('⚠️ [CAMPOS NÃO CHAVE] Validação falhou:', {
                backendUrl: !!backendUrl,
                hasToken: !!auth?.Token,
                corporacaoNome: corporacaoNome
            });
            return [];
        }

        const encodedCorp = encodeURIComponent(corporacaoNome);
        const endpoint = `/api/associacao/campos/corporacao${encodedCorp}`;
        const fullUrl = `${backendUrl}${endpoint}`;

        console.log('🔵 [CAMPOS NÃO CHAVE] Fazendo requisição GET para:', fullUrl);

        try {
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                }
            });

            console.log('📊 [CAMPOS NÃO CHAVE] Status da resposta:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ [CAMPOS NÃO CHAVE] Erro na resposta:', response.status, errorText);
                throw new Error(errorText || 'Erro ao buscar campos não chave');
            }

            let data = await response.json();
            console.log('✅ [CAMPOS NÃO CHAVE] Dados brutos recebidos:', data);

            if (typeof data === 'string') {
                console.log('🔄 [CAMPOS NÃO CHAVE] Dados são string, tentando parse...');
                try {
                    data = JSON.parse(data);
                    console.log('✅ [CAMPOS NÃO CHAVE] Parse bem-sucedido:', data);
                } catch (e) {
                    console.error('❌ [CAMPOS NÃO CHAVE] Erro no parse:', e);
                    data = [];
                }
            }

            if (!Array.isArray(data)) {
                console.warn('⚠️ [CAMPOS NÃO CHAVE] Dados não são um array:', typeof data, data);
                return [];
            }

            console.log('📋 [CAMPOS NÃO CHAVE] Total de campos recebidos:', data.length);
            console.log('📋 [CAMPOS NÃO CHAVE] Primeiro campo (exemplo):', data[0]);

            const camposNaoChave = data.filter((campo: any) => {
                const isKey = Number(campo?.IsKeyColumn) === 1;
                const isNonKey = !isKey;
                console.log('🔍 [CAMPOS NÃO CHAVE] Verificando campo:', {
                    LabelKey: campo?.LabelKey,
                    LabelValue: campo?.LabelValue,
                    IsKeyColumn: campo?.IsKeyColumn,
                    isNonKey
                });
                return isNonKey;
            });

            console.log('🔑 [CAMPOS NÃO CHAVE] Campos não chave encontrados:', camposNaoChave.length);
            console.log('🔑 [CAMPOS NÃO CHAVE] Exemplo:', camposNaoChave[0]);

            // Armazenar informações completas dos campos
            const camposInfoMap = new Map<string, ICampoAssociacaoAPI>();
            camposNaoChave.forEach((campo: any) => {
                if (campo?.LabelKey) {
                    camposInfoMap.set(campo.LabelKey, campo as ICampoAssociacaoAPI);
                }
            });
            setCamposInfo(camposInfoMap);

            const options = camposNaoChave
                .map((campo: any) => {
                    const labelKey = campo?.LabelKey || '';
                    const labelValue = campo?.LabelValue || '';

                    let label = '';
                    if (labelKey && labelValue) {
                        label = `${labelKey} - ${labelValue}`;
                    } else if (labelKey) {
                        label = labelKey;
                    } else if (labelValue) {
                        label = labelValue;
                    }

                    const option = {
                        label,
                        value: labelKey
                    };

                    console.log('📝 [CAMPOS NÃO CHAVE] Criando opção:', option);
                    return option;
                })
                .filter((opt: { label: string; value: string }) => {
                    const isValid = !!opt.value && !!opt.label;
                    if (!isValid) {
                        console.warn('⚠️ [CAMPOS NÃO CHAVE] Opção inválida descartada:', opt);
                    }
                    return isValid;
                });

            console.log('✅ [CAMPOS NÃO CHAVE] Total de opções finais:', options.length);
            console.log('✅ [CAMPOS NÃO CHAVE] Opções finais:', options);
            console.log('📋 [CAMPOS INFO] Informações dos campos armazenadas:', Array.from(camposInfoMap.keys()));

            return options;
        } catch (error: any) {
            console.error('❌ [CAMPOS NÃO CHAVE] Erro na busca:', error);
            throw error;
        }
    }, [auth?.Token, backendUrl]);

    // Debug: monitorar mudanças no estado camposChave
    // Debug: monitorar mudanças no estado camposChave
    useEffect(() => {
        console.log('🔄 [CAMPO CHAVE] Estado camposChave mudou:', camposChave.length, 'opções');
        if (camposChave.length > 0) {
            console.log('📋 [CAMPO CHAVE] Opções no estado (chave = 1):', camposChave);
        }
    }, [camposChave]);

    // Debug: monitorar mudanças no estado camposNaoChave
    useEffect(() => {
        console.log('🔄 [CAMPOS NÃO CHAVE] Estado camposNaoChave mudou:', camposNaoChave.length, 'opções');
        if (camposNaoChave.length > 0) {
            console.log('📋 [CAMPOS NÃO CHAVE] Opções no estado (chave = 0):', camposNaoChave);
        }
    }, [camposNaoChave]);

    // Função para buscar campos da API para filtro histórico (apenas com corporação)
    const fetchCamposFiltroHistorico = useCallback(async (corporacaoNome: string) => {
        if (!backendUrl || !auth?.Token || !corporacaoNome) {
            return;
        }

        setLoadingCamposFiltroHistorico(true);
        try {
            const encodedCorp = encodeURIComponent(corporacaoNome.trim());
            const endpoint = `/api/associacao/campos/corporacao${encodedCorp}`;

            const fullUrl = `${backendUrl}${endpoint}`;
            console.log('🔵 [FILTRO HISTÓRICO] Buscando campos de:', fullUrl);

            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Erro ao buscar campos: ${response.status} ${response.statusText}`);
            }

            let data = await response.json();
            if (typeof data === 'string') {
                data = JSON.parse(data);
            }

            if (!Array.isArray(data)) {
                setCamposDisponiveisFiltro([]);
                return;
            }

            // Converter para o formato ICampoAssociacaoAPI
            const campos: ICampoAssociacaoAPI[] = data.map((item: any) => ({
                LabelKey: item.LabelKey || '',
                LabelValue: item.LabelValue || item.LabelKey || '',
                LabelType: item.LabelType || 'Texto',
                LabelLength: item.LabelLength || 0,
                LabelMask: item.LabelMask || '',
                IsKeyColumn: item.IsKeyColumn || 0
            }));

            setCamposDisponiveisFiltro(campos);
            console.log('✅ [FILTRO HISTÓRICO] Campos recebidos:', campos.length);
        } catch (error) {
            console.error('❌ [FILTRO HISTÓRICO] Erro ao buscar campos:', error);
            setCamposDisponiveisFiltro([]);
        } finally {
            setLoadingCamposFiltroHistorico(false);
        }
    }, [backendUrl, auth?.Token]);

    // Função para buscar campos não-chave para filtros do histórico (mantida para compatibilidade)
    const fetchCamposFiltro = useCallback(async (corporacaoNome: string, organizacaoNome?: string) => {
        if (!backendUrl || !auth?.Token || !corporacaoNome) {
            return;
        }

        setLoadingCamposFiltro(true);
        try {
            const encodedCorp = encodeURIComponent(corporacaoNome);
            let endpoint = `/api/associacao/campos/corporacao${encodedCorp}`;

            if (organizacaoNome) {
                const encodedOrg = encodeURIComponent(organizacaoNome);
                endpoint = `/api/associacao/campos/corporacao${encodedCorp}/organizacao${encodedOrg}`;
            }

            const fullUrl = `${backendUrl}${endpoint}`;
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                }
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar campos para filtros');
            }

            let data = await response.json();
            if (typeof data === 'string') {
                data = JSON.parse(data);
            }

            if (!Array.isArray(data)) {
                data = [];
            }

            // Filtrar apenas campos não-chave
            const camposNaoChave = data.filter((campo: any) => Number(campo?.IsKeyColumn) !== 1);
            setCamposFiltro(camposNaoChave as ICampoAssociacaoAPI[]);
        } catch (error) {
            console.error('Erro ao buscar campos para filtros:', error);
            setCamposFiltro([]);
        } finally {
            setLoadingCamposFiltro(false);
        }
    }, [backendUrl, auth?.Token]);

    // Buscar campos da API quando abrir o sidebar ou mudar corporação (apenas corporação)
    useEffect(() => {
        if (filtrosSidebarOpen && selectedCorporationName) {
            fetchCamposFiltroHistorico(selectedCorporationName);
            // Limpar seleções ao abrir
            setCampoSelecionadoFiltro('');
            setCampoSelecionadoInfo(null);
            setValorDigitadoFiltro('');
            setErroValidacaoFiltro('');
        }
    }, [filtrosSidebarOpen, selectedCorporationName, fetchCamposFiltroHistorico]);

    // Quando um campo for selecionado, atualizar informações do campo
    useEffect(() => {
        if (!campoSelecionadoFiltro) {
            setCampoSelecionadoInfo(null);
            setValorDigitadoFiltro('');
            setErroValidacaoFiltro('');
            return;
        }

        const campoInfo = camposDisponiveisFiltro.find(c => c.LabelKey === campoSelecionadoFiltro);
        setCampoSelecionadoInfo(campoInfo || null);
        setValorDigitadoFiltro('');
        setErroValidacaoFiltro('');
    }, [campoSelecionadoFiltro, camposDisponiveisFiltro]);


    // Função para limpar todos os filtros
    const limparFiltros = useCallback(() => {
        setFiltrosAplicados({});
        setCampoSelecionadoFiltro('');
        setCampoSelecionadoInfo(null);
        setValorDigitadoFiltro('');
        setErroValidacaoFiltro('');
    }, []);

    // Função para atualizar um filtro específico
    const atualizarFiltro = useCallback((campoKey: string, valor: any) => {
        setFiltrosAplicados((prev) => {
            const novo = { ...prev };

            // Se o valor for um objeto (range de números/datas), verificar se está vazio
            if (typeof valor === 'object' && valor !== null) {
                const isEmpty = Object.values(valor).every(v => v === '' || v === null || v === undefined);
                if (isEmpty) {
                    delete novo[campoKey];
                } else {
                    novo[campoKey] = valor;
                }
            } else if (valor === '' || valor === null || valor === undefined || valor === false) {
                delete novo[campoKey];
            } else {
                novo[campoKey] = valor;
            }

            return novo;
        });
    }, []);

    // Carregar campos NÃO chave quando abrir o modal de edição de detalhes
    useEffect(() => {
        console.log('🔄 [CAMPOS NÃO CHAVE] useEffect de edição disparado', {
            openModal,
            selectedCorporationName,
            hasBackendUrl: !!backendUrl,
            hasToken: !!auth?.Token
        });

        if (!openModal) {
            console.log('⏸️ [CAMPOS NÃO CHAVE] Modal de edição fechado, limpando estado');
            setCamposNaoChave([]);
            setLoadingCamposNaoChave(false);
            return;
        }

        const corporacaoNome = selectedCorporationName?.trim();
        if (!corporacaoNome || !backendUrl || !auth?.Token) {
            console.warn('⚠️ [CAMPOS NÃO CHAVE] Condições não atendidas para edição:', {
                corporacaoNome: !!corporacaoNome,
                backendUrl: !!backendUrl,
                hasToken: !!auth?.Token
            });
            setCamposNaoChave([]);
            setLoadingCamposNaoChave(false);
            return;
        }

        let cancelled = false;

        const loadCampos = async () => {
            try {
                console.log('🚀 [CAMPOS NÃO CHAVE] Buscando campos para modal de edição...');
                setLoadingCamposNaoChave(true);
                setLoadingDetalhesExistentes(true);

                // Buscar campos não chave e detalhes existentes em paralelo
                const [options, detalhesExistentes] = await Promise.all([
                    fetchCamposNaoChaveFromApi(corporacaoNome),
                    associacaoEditando
                        ? fetchDetalhesExistentes(
                            corporacaoNome,
                            selectedOrganizationName,
                            associacaoEditando.ativo
                        )
                        : Promise.resolve([])
                ]);

                if (cancelled) {
                    console.log('⏹️ [CAMPOS NÃO CHAVE] Requisição cancelada (modal fechado)');
                    return;
                }

                console.log('✅ [CAMPOS NÃO CHAVE] Opções carregadas para edição:', options.length, options);
                console.log('✅ [DETALHES EXISTENTES] Detalhes encontrados:', detalhesExistentes.length, detalhesExistentes);

                setCamposNaoChave(options);

                // Inicializar formData com os campos não chave
                // Se houver detalhes existentes, preencher com os valores
                const initialFormData: FormDataDetalhes = {};
                const camposExistentesSet = new Set<string>();

                options.forEach((opt) => {
                    const labelKey = opt.value; // LabelKey do campo
                    // Procurar se existe um detalhe para este campo
                    const detalheExistente = detalhesExistentes.find(
                        (d) => d.Campo === labelKey
                    );

                    if (detalheExistente) {
                        // Campo já existe, preencher com o valor existente
                        initialFormData[labelKey] = detalheExistente.Valor || '';
                        camposExistentesSet.add(labelKey);
                        console.log(`✅ [DETALHES EXISTENTES] Campo "${labelKey}" encontrado com valor: "${detalheExistente.Valor}"`);
                    } else {
                        // Campo novo, deixar vazio
                        initialFormData[labelKey] = '';
                    }
                });

                setFormData(initialFormData);
                setCamposExistentes(camposExistentesSet);
                console.log('✅ [CAMPOS NÃO CHAVE] FormData inicializado:', initialFormData);
                console.log('✅ [DETALHES EXISTENTES] Campos existentes:', Array.from(camposExistentesSet));
            } catch (error) {
                if (!cancelled) {
                    console.error('❌ [CAMPOS NÃO CHAVE] Erro ao carregar campos para edição:', error);
                    setCamposNaoChave([]);
                    setFormData({});
                    setCamposExistentes(new Set());
                }
            } finally {
                if (!cancelled) {
                    setLoadingCamposNaoChave(false);
                    setLoadingDetalhesExistentes(false);
                }
            }
        };

        loadCampos();

        return () => {
            console.log('🧹 [CAMPOS NÃO CHAVE] Cleanup do useEffect de edição');
            cancelled = true;
        };
    }, [openModal, selectedCorporationName, selectedOrganizationName, associacaoEditando, backendUrl, auth?.Token, fetchCamposNaoChaveFromApi, fetchDetalhesExistentes]);

    useEffect(() => {
        console.log('🔄 [CAMPO CHAVE] Modal mudou:', openAddModal);
        if (!openAddModal) {
            console.log('🔄 [CAMPO CHAVE] Modal fechado, limpando estado');
            lastCamposChaveCorpRef.current = null;
            setCamposChave([]);
            setLoadingCamposChave(false);
        } else {
            // Quando o modal abre, resetar o ref para permitir busca novamente se necessário
            // Isso garante que se a corporação já estiver preenchida, a busca será feita
            if (novaAssociacao.Corporacao) {
                console.log('🔄 [CAMPO CHAVE] Modal aberto com corporação:', novaAssociacao.Corporacao);
                // Resetar o ref para forçar busca se necessário
                lastCamposChaveCorpRef.current = null;
            }
        }
    }, [openAddModal, novaAssociacao.Corporacao]);

    useEffect(() => {
        const fetchModalLabels = async () => {
            if (!openAddModal || !selectedCorporationName || !backendUrl || !auth?.Token) return;

            // Evita buscar se já tiver carregado para essa corporação (opcional, mas bom para performance)
            if (Object.keys(addModalLabels).length > 0 && lastCamposChaveCorpRef.current === selectedCorporationName) return;

            try {
                const encodedCorp = encodeURIComponent(selectedCorporationName);
                const endpoint = `/api/labels/ADICIONAR_ASSOCIACAO/${encodedCorp}`;
                
                const response = await httpClient.request({
                    method: RequestMethod.GET,
                    endpoint: endpoint
                });

                if (response.success && Array.isArray(response.payload)) {
                    // Converte o array [{Field: 'x', Title: 'y'}] em objeto {'x': 'y'}
                    const labelsMap: Record<string, string> = {};
                    response.payload.forEach((item: any) => {
                        if (item.Field && item.Title) {
                            labelsMap[item.Field] = item.Title;
                        }
                    });
                    setAddModalLabels(labelsMap);
                    console.log("✅ Labels Modal ADICIONAR_ASSOCIACAO carregados:", labelsMap);
                }
            } catch (error) {
                console.error("❌ Erro ao carregar labels do modal:", error);
            }
        };

        fetchModalLabels();
    }, [openAddModal, selectedCorporationName, backendUrl, auth?.Token, httpClient]);

    useEffect(() => {
        console.log('🔄 [CAMPO CHAVE] useEffect disparado', {
            openAddModal,
            corporacaoNome: novaAssociacao.Corporacao,
            hasBackendUrl: !!backendUrl,
            hasToken: !!auth?.Token,
            lastCorpRef: lastCamposChaveCorpRef.current,
            camposChaveLength: camposChave.length
        });

        if (!openAddModal) {
            console.log('⏸️ [CAMPO CHAVE] Modal não está aberto, ignorando');
            return;
        }

        const corporacaoNome = novaAssociacao.Corporacao?.trim();

        if (!corporacaoNome) {
            console.warn('⚠️ [CAMPO CHAVE] Corporação não informada');
            lastCamposChaveCorpRef.current = null;
            setCamposChave([]);
            setLoadingCamposChave(false);
            setNovaAssociacao((prev) => ({ ...prev, CampoChave: '' }));
            return;
        }

        if (!backendUrl || !auth?.Token) {
            console.warn('⚠️ [CAMPO CHAVE] Condições não atendidas:', {
                backendUrl: !!backendUrl,
                hasToken: !!auth?.Token
            });
            lastCamposChaveCorpRef.current = null;
            setCamposChave([]);
            setLoadingCamposChave(false);
            return;
        }

        if (lastCamposChaveCorpRef.current === corporacaoNome && camposChave.length > 0) {
            console.log('⏭️ [CAMPO CHAVE] Corporação já foi buscada, verificando se precisa preencher campo chave');
            // Se já foi buscado e temos campos, sempre garantir que o campo chave está preenchido
            const campoChaveAtual = novaAssociacao.CampoChave;
            const campoChaveExiste = camposChave.some(opt => opt.value === campoChaveAtual);

            // Se não tem campo chave ou o campo chave atual não existe mais nas opções, usar a primeira
            if (!campoChaveAtual || !campoChaveAtual.trim() || !campoChaveExiste) {
                console.log('🔄 [CAMPO CHAVE] Preenchendo campo chave com primeira opção:', camposChave[0].value);
                setNovaAssociacao((prev) => ({
                    ...prev,
                    CampoChave: camposChave[0].value
                }));
            }
            setLoadingCamposChave(false);
            return;
        }

        // Se já foi buscado mas não temos campos, buscar novamente
        if (lastCamposChaveCorpRef.current === corporacaoNome && camposChave.length === 0) {
            console.log('🔄 [CAMPO CHAVE] Corporação já foi buscada mas não há campos, buscando novamente');
            lastCamposChaveCorpRef.current = null; // Resetar para forçar busca
        }

        console.log('✅ [CAMPO CHAVE] Todas as condições atendidas, iniciando busca');
        lastCamposChaveCorpRef.current = corporacaoNome;

        let cancelled = false;
        const fetchCampos = async () => {
            console.log('🚀 [CAMPO CHAVE] Iniciando fetch de campos');
            setLoadingCamposChave(true);
            try {
                const options = await fetchCamposChaveFromApi(corporacaoNome);

                if (cancelled) {
                    console.log('⏹️ [CAMPO CHAVE] Requisição foi cancelada');
                    return;
                }

                console.log('✅ [CAMPO CHAVE] Opções recebidas no useEffect:', options.length, options);
                setCamposChave(options);

                // Preencher automaticamente o campo chave (sempre selecionar a primeira opção disponível)
                setNovaAssociacao((prev) => {
                    if (options.length > 0) {
                        // Sempre usar a primeira opção disponível (campo chave é único)
                        const campoChaveSelecionado = options[0].value;
                        console.log('🔄 [CAMPO CHAVE] Preenchendo automaticamente com:', campoChaveSelecionado);
                        return {
                            ...prev,
                            CampoChave: campoChaveSelecionado
                        };
                    }

                    // Se não há opções, limpar
                    return {
                        ...prev,
                        CampoChave: ''
                    };
                });
            } catch (error: any) {
                console.error('❌ [CAMPO CHAVE] Erro ao carregar campos chave:', error);
                if (!cancelled) {
                    alert.createMessage({
                        icon: 'error',
                        title: 'Erro',
                        html: error?.message || 'Erro ao carregar campos chave.'
                    });
                    setCamposChave([]);
                }
            } finally {
                if (!cancelled) {
                    console.log('✅ [CAMPO CHAVE] Finalizando loading');
                    setLoadingCamposChave(false);
                }
            }
        };

        fetchCampos();

        return () => {
            console.log('🧹 [CAMPO CHAVE] Cleanup: cancelando requisição');
            cancelled = true;
        };
    }, [alert, fetchCamposChaveFromApi, novaAssociacao.Corporacao, openAddModal]);

    // Garantir que o campo chave seja preenchido quando os campos forem carregados
    useEffect(() => {
        if (openAddModal && camposChave.length > 0 && novaAssociacao.Corporacao) {
            console.log('🔄 [CAMPO CHAVE] Verificando se precisa preencher campo chave', {
                camposChaveLength: camposChave.length,
                campoChaveAtual: novaAssociacao.CampoChave,
                primeiraOpcao: camposChave[0]?.value
            });

            // Verificar se precisa preencher o campo chave
            setNovaAssociacao((prev) => {
                // Se não tem campo chave preenchido ou o valor atual não existe nas opções, preencher com a primeira
                const campoChaveExiste = camposChave.some(opt => opt.value === prev.CampoChave);
                if (!prev.CampoChave || !prev.CampoChave.trim() || !campoChaveExiste) {
                    const novoCampoChave = camposChave[0].value;
                    console.log('✅ [CAMPO CHAVE] Preenchendo campo chave automaticamente:', novoCampoChave);
                    return {
                        ...prev,
                        CampoChave: novoCampoChave
                    };
                }
                console.log('✅ [CAMPO CHAVE] Campo chave já está preenchido corretamente:', prev.CampoChave);
                return prev;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openAddModal, camposChave.length, novaAssociacao.Corporacao]);

    // Calcular o valor selecionado do campo chave
    const selectedCampoChave = useMemo(() => {
        if (!novaAssociacao.CampoChave || camposChave.length === 0) {
            return null;
        }
        return camposChave.find(opt => opt.value === novaAssociacao.CampoChave) || null;
    }, [camposChave, novaAssociacao.CampoChave]);

    // Filtrar organizações disponíveis pela corporação selecionada no modal
    const organizacoesDisponiveisModal = useMemo(() => {
        if (!novaAssociacao.Corporacao || !availableOrganizations || availableOrganizations.length === 0) {
            return [];
        }

        // Encontrar o ID da corporação pelo nome
        const corporacao = availableCorporations.find(c => c.name === novaAssociacao.Corporacao);
        if (!corporacao) {
            return [];
        }

        // Filtrar organizações pela corporação
        return availableOrganizations
            .filter(org => org.Idcorporacao === corporacao.id)
            .map(org => ({
                label: org.OrganizationName,
                value: org.OrganizationName
            }));
    }, [novaAssociacao.Corporacao, availableOrganizations, availableCorporations]);

    const handleOpenAddModal = useCallback(() => {
        // Resetar o ref para permitir busca do campo chave quando o modal abrir
        lastCamposChaveCorpRef.current = null;

        setNovaAssociacao({
            Corporacao: selectedCorporationName || '',
            Organizacao: selectedOrganizationName || '',
            Ativo: '',
            ItemAssociado: '',
            CampoChave: ''
        });
        setOpenAddModal(true);
    }, [selectedCorporationName, selectedOrganizationName]);

    const handleCloseAddModal = useCallback(() => {
        setOpenAddModal(false);
        setSavingAssociacao(false);
        setCamposChave([]);
        setAvailableAtivosModal([]);
        lastOrganizacaoBuscadaRef.current = null;
        setNovaAssociacao({
            Corporacao: '',
            Organizacao: '',
            Ativo: '',
            ItemAssociado: '',
            CampoChave: ''
        });
    }, []);

    // Função para buscar ativos no modal baseado na organização selecionada
    const fetchAtivosModal = useCallback(async (organizacao: string) => {
        if (!organizacao || !organizacao.trim()) {
            setAvailableAtivosModal([]);
            return;
        }

        setLoadingAtivosModal(true);
        try {
            const endpoint = '/Gerenciamento/PowerBI/ObterAtivosByOrg';
            const queryParams = new URLSearchParams();

            if (dataSetNamePBI) {
                queryParams.append('datasetName', dataSetNamePBI);
            }

            queryParams.append('organization', organizacao.trim());

            const queryString = queryParams.toString();
            const fullUrl = `${endpoint}?${queryString}`;

            const response = await httpClient.request({
                method: RequestMethod.GET,
                endpoint: fullUrl,
            });

            if (response.success && response.payload) {
                // Extrair ativos únicos
                const uniqueAtivos = Array.from(
                    new Set(response.payload.map((item: any) => item.Ativo))
                ).filter((ativo): ativo is string =>
                    typeof ativo === 'string' && ativo !== ''
                ).sort();

                const ativosOptions = uniqueAtivos.map(ativo => ({
                    label: ativo,
                    value: ativo
                }));

                setAvailableAtivosModal(ativosOptions);
            } else {
                setAvailableAtivosModal([]);
            }
        } catch (error) {
            console.error('Erro ao buscar ativos no modal:', error);
            setAvailableAtivosModal([]);
        } finally {
            setLoadingAtivosModal(false);
        }
    }, [httpClient, dataSetNamePBI]);

    // Buscar ativos quando a organização mudar no modal
    useEffect(() => {
        if (!openAddModal) {
            setAvailableAtivosModal([]);
            lastOrganizacaoBuscadaRef.current = null;
            return;
        }

        const organizacao = novaAssociacao.Organizacao?.trim();

        // Evitar chamadas duplicadas para a mesma organização
        if (organizacao && lastOrganizacaoBuscadaRef.current !== organizacao) {
            lastOrganizacaoBuscadaRef.current = organizacao;
            fetchAtivosModal(organizacao);
        } else if (!organizacao) {
            setAvailableAtivosModal([]);
            lastOrganizacaoBuscadaRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openAddModal, novaAssociacao.Organizacao]);

    const handleSalvarAssociacao = useCallback(async () => {
        // Validar campos obrigatórios e identificar qual está faltando
        const camposFaltando: string[] = [];

        if (!novaAssociacao.Corporacao || !novaAssociacao.Corporacao.trim()) {
            camposFaltando.push('Corporação');
        }
        if (!novaAssociacao.Organizacao || !novaAssociacao.Organizacao.trim()) {
            camposFaltando.push('Organização');
        }
        if (!novaAssociacao.Ativo || !novaAssociacao.Ativo.trim()) {
            camposFaltando.push('Ativo');
        }
        if (!novaAssociacao.ItemAssociado || !novaAssociacao.ItemAssociado.trim()) {
            camposFaltando.push('Item associado');
        }
        if (!novaAssociacao.CampoChave || !novaAssociacao.CampoChave.trim()) {
            camposFaltando.push('Campo chave');
        }

        if (camposFaltando.length > 0) {
            console.warn('⚠️ Campos faltando:', camposFaltando);
            console.log('📋 Estado atual de novaAssociacao:', novaAssociacao);
            alert.createMessage({
                icon: 'warning',
                title: 'Campos obrigatórios',
                html: `Preencha todos os campos para adicionar a associação.<br/>Campos faltando: ${camposFaltando.join(', ')}`
            });
            return;
        }

        if (!backendUrl || !auth?.Token) {
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: 'Não foi possível identificar o backend ou usuário autenticado.'
            });
            return;
        }

        setSavingAssociacao(true);
        try {
            const encodedCorporacao = encodeURIComponent(novaAssociacao.Corporacao.trim());
            const encodedOrganizacao = encodeURIComponent(novaAssociacao.Organizacao.trim());
            const endpoint = `/api/associacoes/${encodedCorporacao}/${encodedOrganizacao}/Associacoes/inserir`;

            const response = await fetch(`${backendUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${auth.Token}`
                },
                body: JSON.stringify(novaAssociacao)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Erro ao inserir associação');
            }

            alert.createMessage({
                icon: 'success',
                title: 'Associação criada',
                html: 'A associação foi adicionada com sucesso.'
            });

            setOpenAddModal(false);
            setAssociacoesRefreshKey((prev) => prev + 1);
        } catch (error: any) {
            console.error('Erro ao salvar associação:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                html: error?.message || 'Erro ao adicionar associação.'
            });
        } finally {
            setSavingAssociacao(false);
        }
    }, [alert, auth?.Token, backendUrl, novaAssociacao]);

    const loadCamposChaveOptions = useCallback(async (inputValue: string) => {
        console.log('🔍 [CAMPO CHAVE] loadCamposChaveOptions chamado com inputValue:', inputValue);
        const corporacaoNome = novaAssociacao.Corporacao?.trim();

        if (!corporacaoNome) {
            console.warn('⚠️ [CAMPO CHAVE] loadCamposChaveOptions: corporação não disponível');
            return [];
        }

        try {
            console.log('🔍 [CAMPO CHAVE] Buscando opções para corporação:', corporacaoNome);
            const options = await fetchCamposChaveFromApi(corporacaoNome);
            console.log('✅ [CAMPO CHAVE] Opções recebidas em loadCamposChaveOptions:', options.length, options);

            if (!inputValue) {
                console.log('✅ [CAMPO CHAVE] Sem inputValue, retornando todas as opções');
                return options;
            }

            const search = inputValue.toLowerCase();
            const filtered = options.filter((opt) => opt.label.toLowerCase().includes(search));
            console.log('✅ [CAMPO CHAVE] Opções filtradas:', filtered.length, filtered);
            return filtered;
        } catch (error: any) {
            console.error('❌ [CAMPO CHAVE] Erro em loadCamposChaveOptions:', error);
            return [];
        }
    }, [fetchCamposChaveFromApi, novaAssociacao.Corporacao]);

    const dateRangeHeaderFilterEditor = useCallback((cell: any, onRendered: any, success: any, cancel: any) => {
        const container = document.createElement('span');
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.gap = '4px';

        const startInput = document.createElement('input');
        startInput.type = 'date';
        startInput.style.padding = '4px';

        const endInput = document.createElement('input');
        endInput.type = 'date';
        endInput.style.padding = '4px';

        const update = () => success({ date1: startInput.value, date2: endInput.value });
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                update();
            }
            if (event.key === 'Escape') {
                cancel();
            }
        };

        [startInput, endInput].forEach((input) => {
            input.addEventListener('change', update);
            input.addEventListener('blur', update);
            input.addEventListener('keydown', handleKey);
        });

        container.appendChild(startInput);
        container.appendChild(endInput);
        return container;
    }, []);

    // Função para buscar movimentações do histórico selecionado



    const normalizeDateValue = (value?: string | null) => {
        if (!value) return null;

        const dateTimeMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
        if (dateTimeMatch) {
            const [, day, month, year, hours, minutes, seconds] = dateTimeMatch;
            return new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10),
                parseInt(hours, 10),
                parseInt(minutes, 10),
                parseInt(seconds, 10)
            );
        }

        const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dateMatch) {
            const [, day, month, year] = dateMatch;
            return new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10)
            );
        }

        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    const dateRangeHeaderFilterFunc = useCallback((headerValue: { date1?: string; date2?: string }, rowValue?: string) => {
        if (!headerValue || (!headerValue.date1 && !headerValue.date2)) return true;
        if (!rowValue) return false;

        const rowDate = normalizeDateValue(rowValue);
        if (!rowDate) return false;

        // Função auxiliar para criar data local a partir da string YYYY-MM-DD
        const createLocalDate = (dateStr: string, isEnd: boolean) => {
            if (!dateStr) return null;
            const [year, month, day] = dateStr.split('-').map(Number);
            // Mês em JS é 0-indexed (0-11)
            const date = new Date(year, month - 1, day);

            if (isEnd) {
                date.setHours(23, 59, 59, 999);
            } else {
                date.setHours(0, 0, 0, 0);
            }
            return date;
        };

        const startDate = headerValue.date1 ? createLocalDate(headerValue.date1, false) : null;
        const endDate = headerValue.date2 ? createLocalDate(headerValue.date2, true) : null;

        if (startDate && endDate) {
            return rowDate >= startDate && rowDate <= endDate;
        }

        if (startDate) return rowDate >= startDate;
        if (endDate) return rowDate <= endDate;

        return true;
    }, []);


    const getAssociacaoBaseColumns = (): ColumnDefinition[] => [
        {
            title: "Ativo", 
            field: 'ativo',
            sorter: 'string',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar ativo',
            width: 410
        },
        {
            title: "Item Associado",
            field: 'veiculo',
            sorter: 'string',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar veículo',
            width: 410
        },
        {
            title: "Data da Associação",
            field: 'associadoEm',
            sorter: 'string',
            headerFilter: dateRangeHeaderFilterEditor,
            headerFilterFunc: dateRangeHeaderFilterFunc,
            headerFilterPlaceholder: 'Filtrar data',
            width: 410
        },
        {
            title: "Tempo Decorrido",
            field: 'tempo',
            sorter: 'string',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar tempo',
            formatter: (cell) => tabulatorHandlersRef.current.formatarTempo((cell.getValue() as string) || ''),
            width: 410
        },
        {
            title: 'Ações',
            field: 'acoes',
            width: 250,
            hozAlign: 'center',
            cssClass: 'coluna-acoes-bottom',
            headerSort: false,
            formatter: (cell) => {
                const data = cell.getRow().getData() as AssociacaoAtivo;
                const associarButton = data.desassociadoEm
                    ? ''
                    : `<button class="tabulator-action-button secondary" data-action="associate">Desassociar</button>`;
                return `
                    <div class="tabulator-action-buttons">
                        <button class="tabulator-action-button" data-action="edit">Editar</button>
                        ${associarButton}
                    </div>
                `;
            },
            cellClick: (event, cell) => {
                const button = (event.target as HTMLElement).closest('button');
                if (!button) return;
                const action = button.getAttribute('data-action');
                const data = cell.getRow().getData() as AssociacaoAtivo;

                if (action === 'edit') {
                    tabulatorHandlersRef.current.handleEditAssociacao(data);
                } else if (action === 'associate') {
                    tabulatorHandlersRef.current.handleAssociar(data);
                }
            }
        }
    ];

    useEffect(() => {
        // Guard: Se a div da tabela não existe ou o Tabulator já foi instanciado, retorna.
        if (!tableRef.current || tabulatorRef.current) return;

        const initializeTable = async () => {
            console.log("🔧 Inicializando Tabela de Associações...");

            // 1. Identificar Corporação
            const corporacaoName = selectedCorporationName || "";
            let colunasConfiguradas: any[] = [];

            // 2. Buscar Labels na API
            if (corporacaoName && backendUrl && auth?.Token) {
                try {
                    const encodedCorp = encodeURIComponent(corporacaoName);
                    const endpoint = `/api/labels/ASSOCIACAO_ATIVOS/${encodedCorp}`;
                    
                    const response = await httpClient.request({
                        method: RequestMethod.GET,
                        endpoint: endpoint
                    });

                    if (response.success && response.payload) {
                        colunasConfiguradas = response.payload;
                        console.log("✅ Labels ASSOCIACAO_ATIVOS carregados:", colunasConfiguradas);
                    }
                } catch (error) {
                    console.error("❌ Erro ao carregar labels da tabela:", error);
                }
            }

            // 3. Obter colunas base
            const baseColumns = getAssociacaoBaseColumns();

            // 4. Merge: Aplicar configuração da API
            // Forçamos o tipo ColumnDefinition[] para evitar erro no construtor do Tabulator
            const finalColumns: ColumnDefinition[] = baseColumns.map(col => {
                const config = colunasConfiguradas.find(
                    c => c.Field && col.field && c.Field.toLowerCase() === col.field.toLowerCase()
                );

                if (config && config.Title) {
                    return { ...col, title: config.Title };
                }
                return col;
            });

            // 5. Inicializar Tabulator
            // O '!' em tableRef.current! diz ao TS que temos certeza que não é null (pois checamos na primeira linha do useEffect)
            if (tableRef.current) {
                tabulatorRef.current = new Tabulator(tableRef.current, {
                    data: associacoes, 
                    layout: 'fitColumns',
                    height: 520,
                    placeholder: 'Nenhuma associação encontrada.',
                    columnHeaderVertAlign: 'bottom',
                    resizableColumnFit: true,
                    movableColumns: true,
                    columns: finalColumns 
                });

                // 6. Configurar Eventos
                tabulatorRef.current.on("rowClick", (e, row) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('button') || target.closest('.tabulator-action-buttons')) {
                        return;
                    }
                    const data = row.getData() as AssociacaoAtivo;
                    if (tabulatorHandlersRef.current && tabulatorHandlersRef.current.handleHistoricoRowClick) {
                        tabulatorHandlersRef.current.handleHistoricoRowClick(data as any);
                    }
                });
            }
        };

        initializeTable();
        return () => {
            if (tabulatorRef.current) {
                tabulatorRef.current.destroy();
                tabulatorRef.current = null;
            }
        };

    }, [dateRangeHeaderFilterEditor, dateRangeHeaderFilterFunc, selectedCorporationName, associacoes]);


    // Atualizar tabela quando voltar para a aba de associações
    useEffect(() => {
        if (activeTab === 0 && tabulatorRef.current && associacoes.length > 0) {
            // Pequeno delay para garantir que o DOM está pronto
            setTimeout(() => {
                if (tabulatorRef.current) {
                    tabulatorRef.current.redraw(true);
                }
            }, 100);
        }
    }, [activeTab, associacoes]);

    const parseDataBrasileira = (dataStr: string | null): Date | null => {
        if (!dataStr) return null;
        const match = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
        if (match) {
            const [, day, month, year, hour, minute, second] = match;
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
        }
        const date = new Date(dataStr);
        return isNaN(date.getTime()) ? null : date;
    };

    const formatarPermanencia = (minutos: number): string => {
        if (!minutos && minutos !== 0) return '-';
        if (minutos === 0) return '< 1 min';
        
        const horas = Math.floor(minutos / 60);
        const mins = minutos % 60;
        const dias = Math.floor(horas / 24);
        const horasRestantes = horas % 24;

        if (dias > 0) {
            const partes: string[] = [];
            if (dias > 0) partes.push(`${dias} dia${dias > 1 ? 's' : ''}`);
            if (horasRestantes > 0) partes.push(`${horasRestantes} hs`);
            if (mins > 0) partes.push(`${mins} min`);
            return partes.join(' e ');
        } else if (horas > 0) {
            const partes: string[] = [];
            partes.push(`${horas} hs`);
            if (mins > 0) partes.push(`${mins} min`);
            return partes.join(' e ');
        } else {
            return `${mins} min`;
        }
    };

    const formatarStatus = (status: string): string => {
        if (!status) return '-';
        const statusConfig: { [key: string]: { text: string; color: string } } = {
            'DentroDoLocal': { text: 'Dentro do Local', color: '#28a745' },
            'Localizado': { text: 'Localizado', color: '#ffc107' },
            'AtivoSemLeitura': { text: 'Ativo Sem Leitura', color: '#dc3545' },
            'DePassagem': { text: 'De Passagem', color: '#6c757d' }
        };
        const config = statusConfig[status];
        if (!config) return status;
        return `
            <div style="display: flex; align-items: center; gap: 8px;">
                <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${config.color}; border: 1px solid ${config.color}; flex-shrink: 0;"></div>
                <span>${config.text}</span>
            </div>
        `;
    };

    // --- Definição das Colunas Base ---
    const getMovimentacoesBaseColumns = (): ColumnDefinition[] => [
        {
            title: isVwco ? "Entrada" : "Data/Entrada",
            field: "PreviousLastSeen",
            sorter: (a: any, b: any) => {
                const toTimestamp = (value: any): number => {
                    if (!value) return 0;
                    const date = parseDataBrasileira(value);
                    return date ? date.getTime() : 0;
                };
                return toTimestamp(a) - toTimestamp(b);
            },
            width: 180,
            formatter: (cell: any) => {
                const value = cell.getValue();
                if (!value) return "Sem data";
                return value; // Já vem no formato brasileiro
            },
        },
        {
            title: isVwco ? "Local" : "Localização",
            field: "Local",
            sorter: "string",
            formatter: (cell: any) => cell.getValue() || '-',
            width: 150,
        },
        {
            title: isVwco ? "Placa" : "Item Associado",
            field: "ItemAssociado",
            sorter: "string",
            formatter: (cell: any) => cell.getValue() || '-',
            width: 150,
        },
        {
            title: "Status",
            field: "Status",
            sorter: "string",
            width: 150,
            formatter: (cell: any) => formatarStatus(cell.getValue()),
        },
        {
            title: isVwco ? "Permanência" : "Tempo de Permanência",
            field: "StayMinutes",
            sorter: "number",
            width: 180,
            formatter: (cell: any) => {
                const minutos = cell.getValue();
                return formatarPermanencia(minutos);
            }
        }
    ];

    // Criar tabela de movimentações no sidebar
    useEffect(() => {
        // Se a referência da div não existe ou a aba não é a correta, retorna.
        if (!movimentacoesTableRef.current || !sidebarVisible || sidebarTab !== 'movimentacoes') return;

        // Limpeza prévia se já existir
        if (movimentacoesTabulatorRef.current) {
            movimentacoesTabulatorRef.current.destroy();
            movimentacoesTabulatorRef.current = null;
        }

        const initializeMovimentacoesTable = async () => {
            console.log("🔧 Inicializando Tabela de Movimentações...");

            // 1. Identificar Corporação
            const corporacaoName = selectedCorporationName || "";
            let colunasConfiguradas: any[] = [];

            // 2. Buscar Labels na API (MOVIMENTACOES)
            if (corporacaoName && backendUrl && auth?.Token) {
                try {
                    const encodedCorp = encodeURIComponent(corporacaoName);
                    // Endpoint específico para esta tela
                    const endpoint = `/api/labels/MOVIMENTACOES/${encodedCorp}`;
                    
                    const response = await httpClient.request({
                        method: RequestMethod.GET,
                        endpoint: endpoint
                    });

                    if (response.success && response.payload) {
                        colunasConfiguradas = response.payload;
                        console.log("✅ Labels MOVIMENTACOES carregados:", colunasConfiguradas);
                    }
                } catch (error) {
                    console.error("❌ Erro ao carregar labels de movimentações:", error);
                }
            }

            // 3. Obter colunas base
            const baseColumns = getMovimentacoesBaseColumns();

            // 4. Merge: Aplicar configuração da API
            const finalColumns: ColumnDefinition[] = baseColumns.map(col => {
                const config = colunasConfiguradas.find(
                    c => c.Field && col.field && c.Field.toLowerCase() === col.field.toLowerCase()
                );

                if (config && config.Title) {
                    return { ...col, title: config.Title };
                }
                return col;
            });

            // 5. Inicializar Tabulator
            if (movimentacoesTableRef.current) {
                movimentacoesTabulatorRef.current = new Tabulator(movimentacoesTableRef.current, {
                    data: movimentacoes, // Passa os dados atuais diretamente
                    layout: 'fitColumns',
                    height: '100%',
                    placeholder: 'Nenhuma movimentação encontrada.',
                    columnHeaderVertAlign: 'bottom',
                    resizableColumnFit: true,
                    columns: finalColumns // Usa colunas dinâmicas
                });
            }
        };

        initializeMovimentacoesTable();

        // Cleanup
        return () => {
            if (movimentacoesTabulatorRef.current) {
                movimentacoesTabulatorRef.current.destroy();
                movimentacoesTabulatorRef.current = null;
            }
        };
    }, [sidebarVisible, movimentacoes, sidebarTab, selectedCorporationName]);

    return (
        <Box sx={{ p: 3, position: 'relative' }}>
            <Card sx={{ position: 'relative', zIndex: sidebarVisible ? 999 : 'auto' }}>
                <CardContent>
                    <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                        Configuração de Associação - Ativos
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Configure os campos personalizados que podem ser associados aos ativos.
                        Selecione uma organização para gerenciar os campos específicos dela.
                    </Typography>

                    {/* Filtros Globais - Corporação e Organização */}
                    <Box sx={{ mb: 3, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, maxWidth: 600, minWidth: 300 }}>
                            <FiltroGlobal />
                        </Box>
                    </Box>

                    {/* Abas de Associações e Histórico */}
                    <Box sx={{ mt: 3, mb: 3 }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="abas de associações">
                                <Tab label="Associações de Ativos" />
                                <Tab label="Histórico" />
                            </Tabs>
                        </Box>

                        {/* Conteúdo da aba Associações de Ativos */}
                        <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                <Typography variant="h6">
                                    Associações de Ativos
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleOpenAddModal}
                                    disabled={!canDisplayAssociacoes}
                                >
                                    Adicionar
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    position: 'relative',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    minHeight: 360,
                                    overflow: 'hidden',
                                    '@media (max-width: 1024px)': {
                                        overflow: 'auto'
                                    }
                                }}
                            >
                                <Box sx={{ opacity: canDisplayAssociacoes ? 1 : 0.4, pointerEvents: canDisplayAssociacoes ? 'auto' : 'none' }}>
                                    <div ref={tableRef} style={{ minHeight: 340 }} />
                                </Box>
                                {!canDisplayAssociacoes && (
                                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.85)', textAlign: 'center', px: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Selecione uma corporação e organização para visualizar as associações.
                                        </Typography>
                                    </Box>
                                )}
                                {loadingAssociacoes && (
                                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)' }}>
                                        <CircularProgress />
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Linhas por página</InputLabel>
                                        <Select
                                            value={linhas}
                                            label="Linhas por página"
                                            onChange={handleLinhasChange}
                                            sx={{
                                                // Verifica se o modo é 'dark'. Se for, texto branco, senão herda a cor padrão.
                                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'inherit',

                                                // Opcional: Para garantir que a setinha (ícone) também fique branca
                                                '& .MuiSvgIcon-root': {
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                                                },

                                                // Garante que o outline (borda) fique visível/correto se necessário
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                                }
                                            }}
                                        >
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={25}>25</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                            onClick={handlePaginaAnterior}
                                            disabled={offset === 0 || loadingAssociacoes}
                                            size="small"
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
                                            Página {getPaginaAtual()} de {getTotalPaginas() || 1}
                                        </Typography>
                                        <IconButton
                                            onClick={handleProximaPagina}
                                            disabled={
                                                loadingAssociacoes ||
                                                (totalAssociacoes > 0 && offset + linhas >= totalAssociacoes) ||
                                                (totalAssociacoes === 0 && associacoes.length < linhas)
                                            }
                                            size="small"
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Conteúdo da aba Histórico - Mantém montado para preservar dados */}
                        <Box sx={{ display: activeTab === 1 ? 'block' : 'none', position: 'relative' }}>
                            <HistoricTab
                                key={`${selectedCorporationName}-${selectedOrganizationName}`}

                                selectedCorporationName={selectedCorporationName}
                                selectedOrganizationName={selectedOrganizationName}
                                canDisplay={canDisplayAssociacoes}
                                isActive={activeTab === 1}
                                onRowClick={handleHistoricoRowClick}
                                filtrosAplicados={filtrosAplicados}
                                onOpenFiltros={() => setFiltrosSidebarOpen(true)}
                                filtrosCount={Object.keys(filtrosAplicados).length}
                            />
                        </Box>
                    </Box>

                </CardContent>
            </Card>

            {/* Modal de Adição de Associação */}
            <Dialog open={openAddModal} onClose={handleCloseAddModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        {/* Título do Modal também pode ser dinâmico se desejar, mas mantive fixo com base no seu código */}
                        Adicionar associação
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        
                        <TextField
                            label="Corporação"
                            fullWidth
                            value={novaAssociacao.Corporacao}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />

                        {/* 2. Organização */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {getAddModalLabel('organizacao', 'Organização')}
                            </Typography>
                            <ReactSelect
                                options={organizacoesDisponiveisModal}
                                value={organizacoesDisponiveisModal.find(opt => opt.value === novaAssociacao.Organizacao) || null}
                                onChange={(option: { label: string; value: string } | null) => {
                                    handleNovaAssociacaoChange('Organizacao', option ? option.value : '');
                                }}
                                isDisabled={!novaAssociacao.Corporacao || organizacoesDisponiveisModal.length === 0}
                                placeholder={
                                    !novaAssociacao.Corporacao 
                                        ? 'Selecione uma corporação primeiro' 
                                        : organizacoesDisponiveisModal.length === 0 
                                            ? `Nenhuma ${getAddModalLabel('organizacao', 'organização').toLowerCase()} disponível` 
                                            : `Selecione ${getAddModalLabel('organizacao', 'a organização').toLowerCase()}`
                                }
                                noOptionsMessage={() => {
                                    if (!novaAssociacao.Corporacao) return 'Selecione uma corporação primeiro';
                                    return `Nenhuma ${getAddModalLabel('organizacao', 'organização').toLowerCase()} disponível`;
                                }}
                                classNamePrefix="react-select"
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                    menu: (base: any) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </Box>

                        {/* 3. Ativo */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                                    {getAddModalLabel('ativo', isVwco ? 'Prisma' : 'Ativo')}
                                </label>
                            </Typography>
                            <ReactSelect
                                options={availableAtivosModal}
                                value={availableAtivosModal.find(opt => opt.value === novaAssociacao.Ativo) || null}
                                onChange={(option: { label: string; value: string } | null) => {
                                    handleNovaAssociacaoChange('Ativo', option ? option.value : '');
                                }}
                                isDisabled={loadingAtivosModal || !novaAssociacao.Organizacao}
                                placeholder={
                                    loadingAtivosModal
                                        ? 'Carregando...'
                                        : novaAssociacao.Organizacao
                                            ? `Selecione ${getAddModalLabel('ativo', isVwco ? 'o prisma' : 'o ativo').toLowerCase()}`
                                            : `Selecione ${getAddModalLabel('organizacao', 'a organização').toLowerCase()} primeiro`
                                }
                                noOptionsMessage={() => {
                                    if (!novaAssociacao.Organizacao) return `Selecione ${getAddModalLabel('organizacao', 'a organização').toLowerCase()} primeiro`;
                                    if (loadingAtivosModal) return 'Carregando...';
                                    return `Nenhum ${getAddModalLabel('ativo', isVwco ? 'prisma' : 'ativo').toLowerCase()} disponível`;
                                }}
                                classNamePrefix="react-select"
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                    menu: (base: any) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </Box>

                         {/* 4. Campo Chave */}
                         {/* Nota: Este campo muitas vezes é escondido ou automático, mas se for exibido como no seu código anterior: */}
                         {/* Se você não usa esse campo na interface visual do seu código original, ignore este bloco. 
                             Mas se houver um select para "Tipo de Associação", use 'campoChave' */}
                         {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                             <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {getAddModalLabel('campoChave', 'Tipo de Associação')}
                             </Typography>
                             ... (ReactSelect do campo chave) ...
                         </Box> 
                         */}

                        {/* 5. Item Associado */}
                        <TextField
                            label={getAddModalLabel('itemAssociado', isVwco ? "Placa / Item Associado" : "Item Associado")}
                            fullWidth
                            value={novaAssociacao.ItemAssociado}
                            onChange={(e) => handleNovaAssociacaoChange('ItemAssociado', e.target.value)}
                            placeholder={`Digite ${getAddModalLabel('itemAssociado', 'o item associado').toLowerCase()}`}
                        />
                        
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseAddModal} variant="outlined" disabled={savingAssociacao}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSalvarAssociacao}
                        variant="contained"
                        disabled={savingAssociacao}
                        sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#12549c' } }}
                    >
                        {savingAssociacao ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Confirmação de Desassociação */}
            <Dialog open={openDesassociarModal} onClose={handleCloseDesassociarModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        Confirmar Desassociação
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {associacaoParaDesassociar && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                            <Typography variant="body1">
                                Tem certeza que deseja desassociar o ativo?
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    <strong>Ativo:</strong> {associacaoParaDesassociar.ativo}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    <strong>Veículo:</strong> {associacaoParaDesassociar.veiculo}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Associado em:</strong> {associacaoParaDesassociar.associadoEm}
                                </Typography>
                                {associacaoParaDesassociar.tempo && (
                                    <Typography variant="body2">
                                        <strong>Tempo de associação:</strong> {formatarTempo(associacaoParaDesassociar.tempo)}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleCloseDesassociarModal}
                        variant="outlined"
                        disabled={desassociando}
                    >
                        Não
                    </Button>
                    <Button
                        onClick={handleConfirmarDesassociar}
                        variant="contained"
                        disabled={desassociando}
                        color="error"
                        sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#c62828' } }}
                    >
                        {desassociando ? 'Desassociando...' : 'Sim, Desassociar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Edição - Informações Adicionais */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        Informações adicionais - {associacaoEditando?.ativo}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {loadingCamposNaoChave || loadingDetalhesExistentes ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ ml: 2 }}>
                                {loadingDetalhesExistentes ? 'Carregando dados existentes...' : 'Carregando campos...'}
                            </Typography>
                        </Box>
                    ) : camposNaoChave.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Nenhum campo não chave disponível para esta corporação.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                            {camposNaoChave.map((campo) => {
                                const labelKey = campo.value; // LabelKey do campo
                                const labelText = campo.label; // "LabelKey - LabelValue" ou apenas um deles
                                const valorAtual = formData[labelKey] || '';
                                const campoInfo = camposInfo.get(labelKey);
                                const mask = campoInfo?.LabelMask || '';
                                const fieldType = campoInfo?.LabelType || '';

                                // Renderizar com máscara se houver
                                if (mask) {
                                    return (
                                        <TextField
                                            key={labelKey}
                                            fullWidth
                                            label={labelText}
                                            value={valorAtual}
                                            onChange={(e) => {
                                                const newValue = applyMask(e.target.value, mask);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    [labelKey]: newValue
                                                }));
                                            }}
                                            placeholder={`Digite o valor para ${labelText} (${mask})`}
                                            disabled={updatingDetalhes}
                                            variant="outlined"
                                            helperText={mask ? `Formato: ${mask}` : undefined}
                                        />
                                    );
                                }

                                // Renderizar sem máscara
                                return (
                                    <TextField
                                        key={labelKey}
                                        fullWidth
                                        label={labelText}
                                        value={valorAtual}
                                        onChange={(e) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                [labelKey]: e.target.value
                                            }));
                                        }}
                                        placeholder={`Digite o valor para ${labelText}`}
                                        disabled={updatingDetalhes}
                                        variant="outlined"
                                        type={fieldType === 'Número' || fieldType === 'Numérico' || fieldType === 'Decimal' ? 'number' : 'text'}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseModal} variant="outlined" disabled={updatingDetalhes}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleUpdate}
                        variant="contained"
                        disabled={updatingDetalhes || loadingCamposNaoChave || loadingDetalhesExistentes || camposNaoChave.length === 0}
                        sx={{ backgroundColor: '#00897b', '&:hover': { backgroundColor: '#00695c' } }}
                    >
                        {updatingDetalhes ? 'Atualizando...' : 'Atualizar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Sidebar de Detalhes (Movimentações e Documentos) */}
            {sidebarVisible && selectedHistorico && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: '50%',
                        height: '100vh',
                        bgcolor: 'background.paper',
                        boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        borderTopLeftRadius: '12px',
                        borderBottomLeftRadius: '12px',
                        overflow: 'hidden' // Importante para o layout interno funcionar
                    }}
                >
                    <div className="details-panel">
                        {/* Header */}
                        <div className="header">
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Detalhes da Associação
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Ativo:</strong> {selectedHistorico.ativo} | <strong>Veículo:</strong> {selectedHistorico.veiculo}
                                </Typography>
                            </Box>
                            <i
                                className="ui close icon close-icon"
                                onClick={() => {
                                    setSidebarVisible(false);
                                    setSelectedHistorico(null);
                                    setMovimentacoes([]);
                                }}
                            />
                        </div>

                        {/* Menu de Abas */}
                        <div className="tab-container">
                            <button
                                className={`tab-button ${sidebarTab === 'movimentacoes' ? 'active' : ''}`}
                                onClick={() => setSidebarTab('movimentacoes')}
                            >
                                <i className="history icon"></i>
                                Movimentações
                            </button>
                            <button
                                className={`tab-button ${sidebarTab === 'documentos' ? 'active' : ''}`}
                                onClick={() => setSidebarTab('documentos')}
                            >
                                <i className="file archive icon"></i>
                                Documentos
                            </button>
                        </div>

                        {/* Conteúdo Aba: Movimentações */}
                        <div className={`tab-content ${sidebarTab === 'movimentacoes' ? 'active' : ''}`} style={{ padding: 0 }}>
                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ flex: 1, p: 2.5, overflow: 'hidden', position: 'relative' }}>
                                    {loadingMovimentacoes ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : movimentacoes.length === 0 ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <Typography variant="body1" color="text.secondary">
                                                Nenhuma movimentação encontrada
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <div ref={movimentacoesTableRef} style={{ height: '100%', width: '100%' }} />
                                    )}
                                </Box>

                                {/* Footer com informações (só aparece na aba movimentações) */}
                                {!loadingMovimentacoes && movimentacoes.length > 0 && (
                                    <Box sx={{
                                        p: 2.5,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'action.hover'
                                    }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            <strong>Associado em:</strong> {selectedHistorico.associadoEm}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            <strong>Desassociado em:</strong> {selectedHistorico.desassociadoEm || 'Ainda associado'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                            <strong>Total de movimentações:</strong> {movimentacoes.length}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </div>

                        {/* Conteúdo Aba: Documentos */}
                        <div className={`tab-content ${sidebarTab === 'documentos' ? 'active' : ''}`}>
                            {sidebarTab === 'documentos' && selectedHistorico && (
                                <GerenciadorArquivosAssociacoes
                                    corporacao={selectedCorporationName}
                                    organizacao={selectedOrganizationName}
                                    idEntidade={(() => {
                                        const ativoEncontrado = listaCompletaAtivos.find(
                                            (a) => a.Ativo === selectedHistorico.ativo
                                        );
                                        return ativoEncontrado ? ativoEncontrado.IdPrism : 0;
                                    })()}

                                    itemAssociado={selectedHistorico.veiculo}
                                    dataAssociacao={selectedHistorico.associadoEm}
                                    backendUrl={backendUrl || ""}
                                    isActive={sidebarTab === 'documentos'}
                                />
                            )}
                        </div>
                    </div>
                </Box>
            )}

            {/* Sidebar de Filtros do Histórico */}
            <Drawer
                anchor="right"
                open={filtrosSidebarOpen}
                onClose={() => setFiltrosSidebarOpen(false)}
                PaperProps={{
                    className: 'filtro-historico-drawer',
                    sx: {
                        width: { xs: '90%', sm: 400 },
                        boxShadow: '-2px 0 8px rgba(0,0,0,0.15)',
                    }
                }}
            >
                <Box className="filtro-historico-container" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header do Sidebar de Filtros */}
                    <Box className="filtro-historico-header" sx={{
                        p: 2.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Filtros do Histórico
                        </Typography>
                        <IconButton
                            onClick={() => setFiltrosSidebarOpen(false)}
                            sx={{ color: 'inherit' }}
                        >
                            <ClearIcon />
                        </IconButton>
                    </Box>

                    {/* Conteúdo dos Filtros */}
                    <Box className="filtro-historico-content" sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        {loadingCamposFiltroHistorico ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : camposDisponiveisFiltro.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Nenhum campo disponível para filtros.
                                </Typography>
                            </Box>
                        ) : (
                            <Box className="filtro-historico-form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Select para escolher o campo */}
                                <FormControl fullWidth size="small" className="filtro-historico-select">
                                    <InputLabel className="filtro-historico-label">Selecione um Campo</InputLabel>
                                    <Select
                                        value={campoSelecionadoFiltro}
                                        label="Selecione um Campo"
                                        onChange={(e) => {
                                            setCampoSelecionadoFiltro(e.target.value);
                                            setValorDigitadoFiltro('');
                                            setErroValidacaoFiltro('');
                                            setFiltrosAplicados({});
                                        }}
                                    >
                                        <MenuItem value="" className="filtro-historico-menu-item">
                                            <em>Nenhum</em>
                                        </MenuItem>
                                        {camposDisponiveisFiltro.map((campo) => (
                                            <MenuItem key={campo.LabelKey} value={campo.LabelKey} className="filtro-historico-menu-item">
                                                {campo.LabelValue || campo.LabelKey}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* TextField para digitar o valor (só aparece quando um campo é selecionado) */}
                                {campoSelecionadoFiltro && campoSelecionadoInfo && (
                                    <TextField
                                        className="filtro-historico-textfield"
                                        fullWidth
                                        size="small"
                                        label={`Valor - ${campoSelecionadoInfo.LabelValue || campoSelecionadoInfo.LabelKey}`}
                                        value={valorDigitadoFiltro}
                                        onChange={(e) => {
                                            let newValue = e.target.value;

                                            // Aplicar máscara se houver
                                            if (campoSelecionadoInfo.LabelMask) {
                                                newValue = applyMask(newValue, campoSelecionadoInfo.LabelMask);
                                            }

                                            setValorDigitadoFiltro(newValue);

                                            // Validar o valor
                                            if (newValue) {
                                                const validacao = validateMaskedValue(
                                                    newValue,
                                                    campoSelecionadoInfo.LabelMask || '',
                                                    campoSelecionadoInfo.LabelType || 'Texto'
                                                );

                                                if (!validacao.valid) {
                                                    setErroValidacaoFiltro(validacao.error || 'Valor inválido');
                                                } else {
                                                    setErroValidacaoFiltro('');
                                                    // Aplicar filtro quando o valor for válido
                                                    atualizarFiltro(campoSelecionadoFiltro, newValue);
                                                }
                                            } else {
                                                setErroValidacaoFiltro('');
                                                atualizarFiltro(campoSelecionadoFiltro, '');
                                            }
                                        }}
                                        error={!!erroValidacaoFiltro}
                                        helperText={
                                            erroValidacaoFiltro ||
                                            (campoSelecionadoInfo.LabelMask ? `Formato: ${campoSelecionadoInfo.LabelMask}` :
                                                campoSelecionadoInfo.LabelType === 'Número' ? 'Digite apenas números' :
                                                    campoSelecionadoInfo.LabelLength ? `Máximo ${campoSelecionadoInfo.LabelLength} caracteres` : '')
                                        }
                                        placeholder={`Digite o valor para ${campoSelecionadoInfo.LabelValue || campoSelecionadoInfo.LabelKey}`}
                                        inputProps={{
                                            maxLength: campoSelecionadoInfo.LabelLength || undefined,
                                            type: campoSelecionadoInfo.LabelType === 'Número' || campoSelecionadoInfo.LabelType === 'Numérico' || campoSelecionadoInfo.LabelType === 'Decimal' ? 'text' : 'text'
                                        }}
                                    />
                                )}

                                {/* Exibir filtro aplicado */}
                                {campoSelecionadoFiltro && valorDigitadoFiltro && !erroValidacaoFiltro && (
                                    <Box className="filtro-historico-chip-container" sx={{ mt: 1, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Filtro aplicado:
                                        </Typography>
                                        <Chip
                                            className="filtro-historico-chip"
                                            label={`${campoSelecionadoInfo?.LabelValue || campoSelecionadoFiltro}: ${valorDigitadoFiltro}`}
                                            onDelete={() => {
                                                setCampoSelecionadoFiltro('');
                                                setCampoSelecionadoInfo(null);
                                                setValorDigitadoFiltro('');
                                                setErroValidacaoFiltro('');
                                                setFiltrosAplicados({});
                                            }}
                                            size="small"
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Footer com ações */}
                    <Box className="filtro-historico-footer" sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        gap: 1
                    }}>
                        <Button
                            className="filtro-historico-btn-limpar"
                            variant="outlined"
                            fullWidth
                            onClick={limparFiltros}
                            disabled={Object.keys(filtrosAplicados).length === 0}
                            startIcon={<ClearIcon />}
                        >
                            Limpar Filtros
                        </Button>
                        <Button
                            className="filtro-historico-btn-aplicar"
                            variant="contained"
                            fullWidth
                            onClick={() => setFiltrosSidebarOpen(false)}
                        >
                            Aplicar
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            {/* Overlay removido - sem efeito escuro */}
            {/* {sidebarVisible && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.3)',
                        zIndex: 998,
                    }}
                    onClick={() => {
                        setSidebarVisible(false);
                        setSelectedHistorico(null);
                        setMovimentacoes([]);
                    }}
                />
            )} */}

            {/* Estilos Mobile para Configuração de Associações */}
            <style>{`
                /* Estilos mobile para Tabulator - Configuração de Associações */
                @media (max-width: 768px) {
                    /* Container da tabela com rolagem horizontal e cursor grab */
                    .tabulator-tableholder {
                        cursor: grab !important;
                        user-select: none !important;
                        -webkit-overflow-scrolling: touch !important;
                    }

                    .tabulator-tableholder:active {
                        cursor: grabbing !important;
                    }

                    /* Largura mínima da tabela para garantir rolagem horizontal */
                    .tabulator-table {
                        min-width: 900px !important;
                    }

                    /* Melhorar legibilidade do texto em mobile */
                    .tabulator-cell {
                        font-size: 13px !important;
                        padding: 10px 8px !important;
                        line-height: 1.4 !important;
                    }

                    .tabulator-col-title {
                        font-size: 13px !important;
                        font-weight: 600 !important;
                        padding: 10px 8px !important;
                    }

                    /* Ajustar filtros do cabeçalho */
                    .tabulator-header-filter input {
                        font-size: 12px !important;
                        padding: 6px 8px !important;
                        min-height: 32px !important;
                    }

                    /* Botões de ação mais legíveis */
                    .tabulator-action-button {
                        font-size: 11px !important;
                        padding: 6px 10px !important;
                        white-space: nowrap !important;
                        min-width: 70px !important;
                    }

                    .tabulator-action-buttons {
                        display: flex !important;
                        gap: 6px !important;
                        justify-content: center !important;
                        flex-wrap: nowrap !important;
                    }

                    /* Larguras específicas das colunas em mobile */
                    .tabulator-col[tabulator-field="ativo"] {
                        min-width: 180px !important;
                    }

                    .tabulator-col[tabulator-field="veiculo"] {
                        min-width: 160px !important;
                    }

                    .tabulator-col[tabulator-field="associadoEm"] {
                        min-width: 180px !important;
                    }

                    .tabulator-col[tabulator-field="tempo"] {
                        min-width: 140px !important;
                    }

                    .tabulator-col[tabulator-field="acoes"] {
                        min-width: 180px !important;
                    }

                    /* Indicador visual de rolagem */
                    .tabulator:after {
                        content: "← Arraste para ver mais →" !important;
                        position: absolute !important;
                        bottom: 10px !important;
                        left: 50% !important;
                        transform: translateX(-50%) !important;
                        background: rgba(0, 0, 0, 0.7) !important;
                        color: white !important;
                        padding: 6px 12px !important;
                        border-radius: 4px !important;
                        font-size: 11px !important;
                        pointer-events: none !important;
                        z-index: 100 !important;
                        white-space: nowrap !important;
                        animation: fadeInOut 3s ease-in-out !important;
                    }

                    @keyframes fadeInOut {
                        0%, 100% { opacity: 0; }
                        10%, 90% { opacity: 1; }
                    }

                    /* Melhorar controles de paginação em mobile */
                    .MuiBox-root > .MuiBox-root {
                        flex-wrap: wrap !important;
                        gap: 12px !important;
                    }

                    .MuiFormControl-root[size="small"] {
                        min-width: 100px !important;
                    }

                    /* Ajustar botão de adicionar */
                    .MuiButton-contained {
                        font-size: 13px !important;
                        padding: 8px 16px !important;
                    }

                    /* Card responsivo */
                    .MuiCard-root {
                        border-radius: 8px !important;
                    }

                    .MuiCardContent-root {
                        padding: 16px !important;
                    }

                    /* Título mais compacto */
                    .MuiTypography-h4 {
                        font-size: 1.5rem !important;
                        margin-bottom: 16px !important;
                    }

                    .MuiTypography-h6 {
                        font-size: 1.1rem !important;
                    }
                }

                /* Estilos para tablets (768px - 1024px) */
                @media (min-width: 768px) and (max-width: 1024px) {
                    /* Container da tabela com rolagem horizontal e cursor grab */
                    .tabulator-tableholder {
                        cursor: grab !important;
                        user-select: none !important;
                        -webkit-overflow-scrolling: touch !important;
                    }

                    .tabulator-tableholder:active {
                        cursor: grabbing !important;
                    }

                    /* Melhorar legibilidade */
                    .tabulator-cell {
                        font-size: 14px !important;
                        padding: 12px 10px !important;
                    }

                    .tabulator-col-title {
                        font-size: 14px !important;
                        font-weight: 600 !important;
                        padding: 12px 10px !important;
                    }

                    /* Botões de ação em tablets */
                    .tabulator-action-button {
                        font-size: 12px !important;
                        padding: 7px 12px !important;
                        white-space: nowrap !important;
                        min-width: 75px !important;
                    }

                    .tabulator-action-buttons {
                        display: flex !important;
                        gap: 8px !important;
                        justify-content: center !important;
                        flex-wrap: nowrap !important;
                    }

                    /* Filtros do cabeçalho */
                    .tabulator-header-filter input {
                        font-size: 13px !important;
                        padding: 7px 10px !important;
                        min-height: 36px !important;
                    }

                    /* Indicador visual de rolagem para tablets */
                    .tabulator:after {
                        content: "← Arraste para ver mais →" !important;
                        position: absolute !important;
                        bottom: 10px !important;
                        left: 50% !important;
                        transform: translateX(-50%) !important;
                        background: rgba(0, 0, 0, 0.7) !important;
                        color: white !important;
                        padding: 6px 12px !important;
                        border-radius: 4px !important;
                        font-size: 12px !important;
                        pointer-events: none !important;
                        z-index: 100 !important;
                        white-space: nowrap !important;
                        animation: fadeInOut 3s ease-in-out !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default MenuAtivosCampos;

